<template lang="html">
  <v-content>
    <error-message-dialog
      :opened.sync="errorMessageDialogOpened"
      :message="errorMessage"
    />

    <ReviewReturnDetailSyncDialog
      :opened.sync="reviewReturnDetailSyncDialogOpened"
      :current-company="currentCompany"
    />

    <OwnerNotificationDialog
      :opened.sync="ownerNotificationDialogOpened"
      :current-company="currentCompany"
      @refresh="getCurrentCompany"
    />

    <ConfirmNewConversationDialog
      v-if="ownersAreChangingAndReadyToChange()"
      :opened.sync="confirmNewConversationDialogOpened"
      :currentOwners="currentCompany.owners"
      @update-owners="updateOwners"
    />

    <v-dialog
      v-model="addOwnerDialogOpened"
      width="600"
      persistent
      @keydown.esc="addOwnerDialogOpened = false"
    >
      <v-card block flat>
        <v-card-title class="pb-0">
          <span class="title">Add New Owner</span>
        </v-card-title>
        <v-card-text>
          <v-layout column>
            <v-layout wrap class="text-xs-left">
              <v-flex xs6 class="px-2">
                <div>First Name</div>
                <v-text-field
                  name="first-name"
                  placeholder="Jack"
                  type="text"
                  v-model="newOwnerFirstName"
                  required
                  :rules="rules.presence"
                  solo
                  class="default-input"
                />
              </v-flex>
              <v-flex xs6 class="px-2">
                <div>Last Name</div>
                <v-text-field
                  name="last-name"
                  placeholder="Smith"
                  type="text"
                  v-model="newOwnerLastName"
                  required
                  :rules="rules.presence"
                  solo
                  class="default-input"
                />
              </v-flex>
              <v-flex xs6 class="px-2">
                <div>Email</div>
                <v-text-field
                  name="email"
                  placeholder="Email"
                  type="email"
                  v-model="newOwnerEmail"
                  :rules="rules.email"
                  required
                  class="default-input"
                  solo
                />
              </v-flex>
              <v-flex xs6 class="px-2">
                <div>Phone number</div>
                <v-text-field
                  name="phone-number"
                  type="phone"
                  mask="phone"
                  placeholder="Optional"
                  v-model="newOwnerPhoneNumber"
                  solo
                  class="default-input"
                />
              </v-flex>
            </v-layout>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="primary text-none"
            @click="openConfirmNewConfirmationDialogFromAddOwner()"
            :disabled="
              isEmpty(newOwnerFirstName) ||
                isEmpty(newOwnerLastName) ||
                isEmpty(newOwnerEmail)
            "
          >
            Add
          </v-btn>
          <v-btn
            class="error--text text-none"
            flat
            @click="addOwnerDialogOpened = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <NotificationSubscriptionDialog
      v-if="currentCompanyId"
      :opened.sync="notificationSubscriptionDialogOpened"
      :selected-entity-ids="[currentCompanyId]"
      @refresh="onNotificationSubscriptionRefresh"
    />

    <create-custom-report-dialog
      v-if="currentCompanyId"
      :opened.sync="createCustomReportDialogOpened"
      :custom-templates="reportTemplates"
      :current-company-id="currentCompanyId"
    />

    <ConfirmationDialog
      v-model="sendLoginLinkConfirmationDialogOpened"
      @confirm="sendClientSignInLink(currentCompany.user.id)"
      type="info"
    >
      Are you sure you want to send the email with a sign-in link?
      {{
        currentCompany.user.passwordless
          ? 'It will prompt the client to reset the password. '
          : ''
      }}The sign-in link will last 24 hours.
    </ConfirmationDialog>

    <ConfirmationDialog
      v-model="lockQuestionnaireConfirmationDialogOpened"
      @confirm="toggleLocked(selectedQuestionnaire)"
    >
      Are you sure you want to lock this questionnaire? Locking prevents any
      additional edits to the questionnaire.
    </ConfirmationDialog>

    <v-dialog
      v-model="lockQuestionnaireAfterExportConfirmationDialogOpened"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      width="600"
      @keydown.esc="
        lockQuestionnaireAfterExportConfirmationDialogOpened = false
      "
    >
      <v-card block flat v-if="selectedQuestionnaire">
        <v-card-title class="title pb-0"> Confirmation </v-card-title>
        <v-card-text class="text-xs-left subheading">
          <v-alert type="error" :value="true" outline>
            Do you want to
            {{
              selectedQuestionnaire.track_changes
                ? ''
                : 'track changes for this questionnaire or '
            }}lock this questionnaire after exporting?
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            v-if="!selectedQuestionnaire.track_changes"
            class="primary--text text-none"
            outline
            @click="toggleTrackChanges(true)"
          >
            Track Changes
          </v-btn>
          <v-btn
            class="primary text-none"
            @click="toggleLocked(selectedQuestionnaire)"
          >
            Lock Questionnaire
          </v-btn>
          <v-btn
            class="error--text text-none"
            flat
            @click="
              lockQuestionnaireAfterExportConfirmationDialogOpened = false
            "
          >
            No
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="selectClientToEditDialogOpened"
      width="600"
      persistent
      @keydown.esc="selectClientToEditDialogOpened = false"
    >
      <v-card block flat>
        <v-card-title class="title">Select Owner</v-card-title>
        <v-card-text>
          <v-layout column>
            <v-select
              class="default-input"
              v-model="selectedOwnerId"
              :items="currentCompany.owners"
              solo
              hide-details
              item-text="full_name"
              placeholder="Select owner to edit"
              item-value="id"
              @change="openEditClientInfoDialog(selectedOwnerId)"
            />
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer class="hidden-xs-only" />
          <v-btn
            :block="$vuetify.breakpoint.xsOnly"
            outline
            class="default-input error--text text-capitalize"
            @click="selectClientToEditDialogOpened = false"
          >
            {{ $t('cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="selectClientToSendLoginLinkDialogOpened"
      width="600"
      persistent
      @keydown.esc="selectClientToSendLoginLinkDialogOpened = false"
    >
      <v-card block flat>
        <v-card-title class="title pb-0">Select User</v-card-title>
        <v-card-text>
          <v-alert type="info" :value="true" outline class="mb-4">
            Select an owner to send sign in link to. If the client has a
            password, it will prompt the client to reset the password. The
            sign-in link will last 24 hours.
          </v-alert>
          <v-layout column>
            <v-select
              class="default-input"
              v-model="selectedOwnerId"
              :items="currentCompany.owners"
              solo
              hide-details
              item-text="full_name"
              placeholder="Select owner"
              item-value="id"
              @change="sendClientSignInLink(selectedOwnerId)"
            />
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer class="hidden-xs-only" />
          <v-btn
            :block="$vuetify.breakpoint.xsOnly"
            outline
            class="default-input error--text text-capitalize"
            @click="selectClientToSendLoginLinkDialogOpened = false"
          >
            {{ $t('cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ManageTagsDialog
      :opened.sync="manageTagsDialogOpened"
      :tagType.sync="manageTagType"
    />

    <SendQuestionnaireDialog
      v-model="sendQuestionnaireDialogOpened"
      @open-send-template-dialog="
        sendTemplateDialogOpened = true;
        newTemplateScheduler = false;
      "
      @open-send-open-items-dialog="sendOpenItemsDialogOpened = true"
      @open-send-document-drop-off-dialog="
        sendDocumentDropOffDialogOpened = true
      "
    />

    <SendTemplateDialog
      v-model="sendTemplateDialogOpened"
      @refresh-questionnaires="refreshQuestionnaires()"
      :questionnaire-loading="tableLoading"
      :current-company="currentCompany"
      :new-template-scheduler="newTemplateScheduler"
    />

    <SendDocumentDropOffDialog
      v-model="sendDocumentDropOffDialogOpened"
      :selected-entity-ids="[currentCompanyId]"
      @refresh-questionnaires="refreshQuestionnaires()"
      :questionnaire-loading="tableLoading"
    />

    <TemplateSchedulerDialog
      v-if="currentCompanyId"
      v-model="templateSchedulerDialogOpened"
      :current-company-id="currentCompanyId"
      @create-template-scheduler="createTemplateScheduler()"
    />

    <SendOpenItemsDialog
      v-if="currentCompanyId"
      v-model="sendOpenItemsDialogOpened"
      @refresh-questionnaires="refreshQuestionnaires()"
      :current-company-id="currentCompanyId"
      :questionnaire-loading="tableLoading"
      :parent-questionnaire="selectedQuestionnaire"
      :questionnaire-dialog-opened="questionnaireDialogOpened"
      :open-item-questionnaire="selectedOpenItemQuestionnaire"
      @refresh-questionnaire-dialog="refreshQuestionnaireDialog"
    />

    <SendQuestionnaireReminderDialog
      :opened.sync="sendQuestionnaireReminderDialogOpened"
      :selected-questionnaires="selectedQuestionnaires"
      :multiple="!isEmpty(selectedQuestionnaires)"
      @refresh-questionnaires="refreshQuestionnaires()"
    />

    <v-dialog
      v-model="internalNotesDialogOpened"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="800"
      persistent
      @keydown.esc="internalNotesDialogOpened = false"
    >
      <InternalNote
        v-if="currentCompanyId"
        :dialog-opened="internalNotesDialogOpened"
        :current-company-id="currentCompanyId"
        @closeDialog="internalNotesDialogOpened = false"
        @get-current-company="getCurrentCompany()"
      />
    </v-dialog>

    <v-dialog
      v-model="priorYearItemsDialogOpened"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      width="800"
      persistent
      @keydown.esc="priorYearItemsDialogOpened = false"
    >
      <v-card block flat>
        <v-card-title class="title pb-0">
          Rollover Items
          <v-spacer />
          <span class="mr-2 body-1">For Tax Year</span>
          <div style="width: 100px" class="mr-2">
            <v-select
              v-model="rollOverYear"
              :items="rollOverYearOptions"
              hide-details
              class="default-input body-1"
              solo
              small
            />
          </div>
          <v-btn
            fab
            small
            class="error--text text-none my-0"
            flat
            @click="priorYearItemsDialogOpened = false"
          >
            <v-icon size="25">clear</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="text-xs-left subheading">
          <v-alert type="info" class="mb-4" :value="true" outline>
            This list contains documents or information the client needs to
            upload or fill out year after year.
            {{
              currentFirm &&
              currentFirm.preferences.prior_year_document_handling ===
                'within_sections'
                ? 'For the documents to be compatible with our rollover item stacking, please use the following format form_name (company_name) ex. W-2 (Soraban)'
                : ''
            }}
          </v-alert>

          <v-layout align-center class="font-weight-bold"
            >Items for {{ rollOverYear }}</v-layout
          >
          <PriorYearItems
            :opened="priorYearItemsDialogOpened"
            :year="rollOverYear"
            :company="currentCompany"
            allow-import
            @close-dialog="priorYearItemsDialogOpened = false"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="editDueDateDialogOpened"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      width="400"
      @keydown.esc="editDueDateDialogOpened = false"
    >
      <v-card block flat v-if="selectedQuestionnaire">
        <v-card-title>
          <span class="title">Edit Reminders</span>
          <v-spacer />
          <!-- <span class="caption error--text">*Required</span> -->
        </v-card-title>
        <v-card-text>
          <v-layout column>
            <v-layout class="text-xs-left">
              <!-- <v-flex xs6 class="pr-2">
                <div>Year</div>
                <v-autocomplete
                  class="default-input body-1"
                  hide-details
                  solo
                  :items="yearOptions"
                  v-model="selectedQuestionnaire.tax_year"
                />
              </v-flex> -->
              <v-flex xs12 class="pr-2">
                <div>
                  Due Date<v-tooltip top
                    ><v-icon slot="activator" size="15" class="ml-1"
                      >info</v-icon
                    ><span
                      >Reminders will be sent out automatically to incomplete
                      questionnaire approaching the due date</span
                    ></v-tooltip
                  >
                </div>
                <v-text-field
                  class="default-input body-1"
                  type="date"
                  solo
                  item-text="name"
                  item-value="id"
                  :rules="rules.dueDate"
                  v-model="selectedQuestionnaire.due_date"
                />
              </v-flex>
            </v-layout>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-switch
            label="Disable Reminders"
            v-model="remindersDisabled"
            hide-details
            :disabled="savingQuestionnaire"
            @change="saveRemindersDisabled"
            class="default-input ma-0 pa-0"
          />
          <v-spacer />
          <v-btn
            class="primary text-none"
            :disabled="!questionnaireSavable || savingQuestionnaire"
            :loading="savingQuestionnaire"
            @click="saveQuestionnaire()"
          >
            Save
          </v-btn>
          <v-btn
            class="error--text text-none"
            flat
            @click="editDueDateDialogOpened = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ConfirmationDialog
      v-model="removeQuestionnaireConfirmationDialogOpened"
      @confirm="deleteQuestionnaire"
    >
      Are you sure you want to remove the questionnaire? You will remove all
      progress done on the questionnaire so far, and the client will not be able
      to access or see the deleted questionnaire. The client will not be
      notified.
    </ConfirmationDialog>

    <SwitchPrimaryContactDialog
      v-if="currentCompanyId"
      v-model="switchPrimaryContactDialogOpened"
      :owner-options="ownerOptions"
      :current-company-id="currentCompanyId"
      @refresh="getCurrentCompany"
    />

    <v-dialog
      v-model="followUpDialogOpened"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      width="600"
      @keydown.esc="followUpDialogOpened = false"
    >
      <v-card block flat>
        <v-card-title class="title"> Confirmation </v-card-title>
        <v-card-text
          class="text-xs-left subheading"
          v-if="selectedQuestionnaire"
        >
          <v-alert type="error" :value="true" outline>
            The last reminder was sent
            {{ moment(selectedQuestionnaire.last_followed_up).format('lll') }}.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="primary text-none"
            @click="sendFollowUp(currentCompany)"
          >
            Yes
          </v-btn>
          <v-btn
            class="error--text text-none"
            flat
            @click="followUpDialogOpened = false"
          >
            No
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <EditClientDialog
      :opened.sync="editClientInfoDialogOpened"
      :selected-client="clientToSave"
      :firm-contact-tags="firmContactTags"
      :use-firm-roles="useFirmRoles"
      :firm-users="firmUsers"
      @refresh="getCurrentCompany()"
      @refresh-questionnaires="refreshQuestionnaires()"
    />

    <v-dialog
      v-model="customElementDialogOpened"
      v-if="entityToSave"
      width="800"
      scrollable
      persistent
      :fullscreen="$vuetify.breakpoint.xsOnly"
    >
      <v-card>
        <v-card-title class="title pb-0">
          Personalization
          <v-spacer />
          <v-btn
            fab
            small
            flat
            class="white error--text text-capitalize ma-0"
            @click="customElementDialogOpened = false"
          >
            <v-icon size="25"> clear </v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-2">
          <v-layout class="pb-3 text-xs-left">
            <v-flex xs12>
              <label class="subheading">Custom Element</label>
            </v-flex>
          </v-layout>
          <v-layout>
            <v-flex xs6 class="pr-2">
              <v-layout align-center class="text-xs-left">
                <div class="dashboard__label">Element HTML</div>
                <v-tooltip top>
                  <v-layout align-center slot="activator">
                    <v-icon size="15" class="ml-2">info</v-icon>
                  </v-layout>
                  <span> Includes a custom component in client dashboard </span>
                </v-tooltip>
              </v-layout>
              <v-textarea
                solo
                label="Custom element"
                v-model="entityToSave.custom_element"
                class="default-input mb-1"
                placeholder="Type html"
              />
            </v-flex>
            <v-flex xs6 class="px-2">
              <v-layout align-center class="text-xs-left">
                <div class="dashboard__label">Preview</div>
              </v-layout>
              <div v-html="entityToSave.custom_element" />
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="primary text-none"
            @click="saveEntity()"
            :loading="entitySaving"
          >
            Save
          </v-btn>
          <v-btn
            class="error--text text-none"
            flat
            @click="customElementDialogOpened = false"
          >
            {{ $t('cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="activityLogDialogOpened"
      width="800"
      scrollable
      :fullscreen="$vuetify.breakpoint.xsOnly"
      @keydown.esc="activityLogDialogOpened = false"
    >
      <v-card>
        <v-card-title class="title">
          Activity Logs for {{ currentCompany.name }}
          <v-spacer />
          <v-btn
            fab
            small
            flat
            class="white error--text text-capitalize ma-0"
            @click="activityLogDialogOpened = false"
          >
            <v-icon size="25"> clear </v-icon>
          </v-btn>
        </v-card-title>
        <v-card-actions>
          <v-select
            label="Filter by activity type"
            v-model="activityLogTypeFilter"
            :items="activitiesLogTypes"
            class="default-input active-type-select"
            clearable
            hide-details
            solo
          />
        </v-card-actions>
        <v-card-text>
          <activity-log
            v-if="currentCompanyId"
            full-height
            :type-id="activityLogTypeFilter"
            :dialog-opened="activityLogDialogOpened"
            :current-company-id="currentCompanyId"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <ExportQuestionnaireDialog
      v-if="selectedQuestionnaire"
      v-model="exportQuestionnaireDialogOpened"
      :current-company="currentCompany"
      :selected-questionnaires="[selectedQuestionnaire]"
      :all-questionnaires="yearQuestionnaires"
      @open-lock-questionnaire-confirmation-dialog="
        openLockQuestionnaireConfirmationDialog()
      "
    />

    <v-dialog
      v-model="questionnaireDialogOpened"
      max-width="1000"
      persistent
      :fullscreen="$vuetify.breakpoint.smAndDown"
      @keydown.esc="questionnaireDialogOpened = false"
    >
      <v-card v-if="selectedQuestionnaire">
        <v-card-title class="pb-0 mb-2">
          <v-layout align-center v-if="$vuetify.breakpoint.smAndUp">
            <div class="title mr-2">
              {{ selectedQuestionnaire.formatted_name
              }}{{
                selectedQuestionnaire.formatted_name.includes(
                  selectedQuestionnaire.tax_year,
                )
                  ? ''
                  : ` (${selectedQuestionnaire.tax_year})`
              }}
              <v-tooltip bottom v-if="selectedQuestionnaire.locked">
                <v-icon slot="activator" class="mx-1 darkgrey--text" size="22"
                  >lock</v-icon
                >
                <span>Locked from further edits</span>
              </v-tooltip>
              <v-tooltip bottom v-if="selectedQuestionnaire.track_changes">
                <v-chip slot="activator" class="mx-1" small
                  >Tracking Changes</v-chip
                >
                <span
                  >All activities in the questionnaire will notified until
                  completion.</span
                >
              </v-tooltip>
              <v-chip class="mx-1" small v-if="selectedQuestionnaire.is_draft"
                >Draft</v-chip
              >
            </div>
            <v-spacer />
            <soraban-action-button
              v-if="!selectedQuestionnaire.is_draft"
              @click="generateQuestionnaireLink(selectedQuestionnaire.id)"
              icon="copy"
              help="Copy Link"
            />
            <soraban-action-button
              @click="sendOpenItemsDialogOpened = true"
              icon="email"
              :help="
                selectedQuestionnaire.locked
                  ? 'Unlock the questionnaire before requesting additional items'
                  : 'Request Additional Items'
              "
              class="success--text"
              :disabled="selectedQuestionnaire.locked"
            />
            <soraban-action-button
              @click="viewParentQuestionnaire()"
              icon="person"
              help="View as Client"
              class="success--text"
            />
            <soraban-action-button
              v-if="!selectedQuestionnaire.is_draft"
              @click="
                selectedQuestionnaire.locked
                  ? toggleLocked(selectedQuestionnaire)
                  : (lockQuestionnaireConfirmationDialogOpened = true)
              "
              :icon="selectedQuestionnaire.locked ? 'lock_open' : 'lock'"
              :help="selectedQuestionnaire.locked ? 'Unlock' : 'Lock'"
              class="success--text"
            />
            <soraban-action-button
              slot="activator"
              icon="get_app"
              class="success--text"
              help="Export"
              @click="openQuestionnaireExportDialog(selectedQuestionnaire)"
            />
            <v-btn
              fab
              small
              flat
              class="white error--text text-capitalize ma-0"
              @click="questionnaireDialogOpened = false"
            >
              <v-icon size="25"> clear </v-icon>
            </v-btn>
          </v-layout>

          <v-layout align-center v-else>
            <div class="title mr-2" style="text-align: left">
              {{ selectedQuestionnaire.formatted_name
              }}{{
                selectedQuestionnaire.formatted_name.includes(
                  selectedQuestionnaire.tax_year,
                )
                  ? ''
                  : ` (${selectedQuestionnaire.tax_year})`
              }}
              <v-tooltip bottom v-if="selectedQuestionnaire.locked">
                <v-icon slot="activator" class="mr-1 darkgrey--text" size="22"
                  >lock</v-icon
                >
                <span>Locked from further edits</span>
              </v-tooltip>
              <v-tooltip bottom v-if="selectedQuestionnaire.track_changes">
                <v-chip slot="activator" class="mx-1" small
                  >Tracking Changes</v-chip
                >
                <span
                  >All activities in the questionnaire will notified until
                  completion.</span
                >
              </v-tooltip>
              <v-chip class="mx-1" small v-if="selectedQuestionnaire.is_draft"
                >Draft</v-chip
              >
            </div>
            <v-spacer />
            <v-menu offset-y>
              <template v-slot:activator="{ on }">
                <v-btn fab small flat class="ma-0" v-on="on">
                  <v-icon size="25"> more_horiz </v-icon>
                </v-btn>
              </template>
              <v-list>
                <v-list-tile
                  v-if="!selectedQuestionnaire.is_draft"
                  @click="
                    selectedQuestionnaire.locked
                      ? toggleLocked(selectedQuestionnaire)
                      : (lockQuestionnaireConfirmationDialogOpened = true)
                  "
                >
                  <v-list-tile-title>{{
                    selectedQuestionnaire.locked ? 'Unlock' : 'Lock'
                  }}</v-list-tile-title>
                </v-list-tile>
                <v-list-tile
                  @click="openQuestionnaireExportDialog(selectedQuestionnaire)"
                >
                  <v-list-tile-title>Export</v-list-tile-title>
                </v-list-tile>
              </v-list>
            </v-menu>
            <v-btn
              fab
              small
              flat
              class="white error--text text-capitalize ma-0"
              @click="questionnaireDialogOpened = false"
            >
              <v-icon size="25"> clear </v-icon>
            </v-btn>
          </v-layout>
        </v-card-title>
        <v-card-text
          v-if="
            !questionnaireLoading &&
              selectedQuestionnaire.questionnaire_questions
          "
          class="py-0"
        >
          <SmartQuestionnaireView
            :current-questionnaire="selectedQuestionnaire"
            :current-company="currentCompany"
            @refresh-current-questionnaire="refreshQuestionnaireDialog"
            :default-tab="smartQuestionnaireViewDefaultTab"
            @refresh-questionnaires="refreshQuestionnaires"
            @close-questionnaire-dialog="questionnaireDialogOpened = false"
          />
        </v-card-text>
        <v-card-text v-else class="py-5">
          <v-progress-circular
            indeterminate
            size="25"
            color="primary"
          ></v-progress-circular>
        </v-card-text>
      </v-card>
    </v-dialog>

    <EditEntityDialog
      v-model="editEntityInfoDialogOpened"
      :selected-entity="entityToSave"
      :selected-user="clientToSave"
      :is-individual-entity="
        entityToSave && !showEntityInformation(currentCompany)
      "
      :new-types="newTypes"
      @loading="tableLoading = $event"
      :firm-users="firmUsers"
      @refresh="getCurrentCompany()"
      @refresh-questionnaires="refreshQuestionnaires()"
      @open-review-return-detail-sync-dialog="
        reviewReturnDetailSyncDialogOpened = true
      "
      admin
    />

    <v-container style="max-width: 1000px">
      <GlobalUiAlert class="mb-2" />
      <v-layout class="mb-2" align-center>
        <h1>Dashboard</h1>
      </v-layout>
      <v-card
        flat
        class="content-container mb-4"
        v-if="showEntityInformation(currentCompany) && pageLoaded"
      >
        <v-card-title class="subheading font-weight-bold">
          Entity Info
          <v-chip
            v-if="currentCompany.inactive"
            class="ml-2"
            small
            color="error"
            text-color="white"
          >
            Inactive
          </v-chip>
          <v-spacer />
          <v-layout justify-end>
            <soraban-action-button
              @click="$router.push(`/dashboard?companyId=${currentCompany.id}`)"
              icon="person"
              help="View as Client"
              class="success--text"
            />
            <soraban-action-button
              @click="openNotificationSubscriptionDialog()"
              icon="notifications_active"
              help="Notification"
              :filled="isSubscribed"
            />
            <soraban-action-button
              @click="internalNotesDialogOpened = true"
              icon="sticky_note_2"
              :help="`View Notes (${currentCompany.note_count})`"
              :filled="currentCompany.note_count > 0"
            />
            <soraban-action-button
              v-if="currentCompany"
              @click="priorYearItemsDialogOpened = true"
              icon="grading"
              :filled="currentCompany.prior_year_items.length > 0"
              help="Edit Rollover Items"
            />
            <soraban-action-button
              @click="activityLogDialogOpened = true"
              icon="schedule"
              help="Activity Logs"
            />
            <v-menu
              transition="slide-y-transition"
              bottom
              right
              nudge-bottom="40"
            >
              <soraban-action-button
                slot="activator"
                icon="more_vert"
                help="More Actions"
              />
              <v-list>
                <v-list-tile @click="openEditEntityDialog()">
                  <v-list-tile-title> Edit Entity Info </v-list-tile-title>
                </v-list-tile>
                <v-list-tile
                  @click="createCustomReportDialogOpened = true"
                  v-if="customReportsEnabled"
                >
                  <v-list-tile-title> Custom Report </v-list-tile-title>
                </v-list-tile>
                <v-list-tile
                  v-if="customElementEnabled"
                  @click="openCustomElementDialog()"
                >
                  <v-list-tile-title> Personalize </v-list-tile-title>
                </v-list-tile>
              </v-list>
            </v-menu>
          </v-layout>
        </v-card-title>
        <v-divider />
        <v-card-text v-if="pageLoaded">
          <v-layout class="mt-2">
            <v-layout column>
              <v-layout row wrap justify-start align-start class="text-xs-left">
                <v-flex xs12 md6 lg4 class="mb-4 pr-4">
                  <div class="dashboard__label">Entity Name</div>
                  <v-layout class="dashboard__value ph-no-capture" align-center>
                    {{ currentCompany.tax_return_name }}
                  </v-layout>
                </v-flex>
                <v-flex xs12 md6 lg4 class="mb-4 pr-4">
                  <div class="dashboard__label">Entity Type</div>
                  <v-layout class="dashboard__value" align-center>
                    {{ currentCompany.entity_type }}
                  </v-layout>
                </v-flex>
                <v-flex
                  xs12
                  md6
                  lg4
                  class="mb-4 pr-4"
                  v-if="!isEmpty(newOwnerIds)"
                >
                  <div class="dashboard__label">Owners</div>
                  <v-autocomplete
                    class="default-input ph-no-capture"
                    v-model="newOwnerIds"
                    :loading="savingOwners"
                    multiple
                    :items="allClients"
                    solo
                    hide-details
                    item-text="full_name"
                    item-value="id"
                    @blur="openConfirmNewConversationDialog()"
                  >
                    <template v-slot:prepend-item>
                      <v-list-tile
                        @mousedown="addOwnerDialogOpened = true"
                        @click="openAddOwnerDialog"
                      >
                        <v-list-tile-action>
                          <v-icon>add</v-icon>
                        </v-list-tile-action>
                        <v-list-tile-content>
                          <v-list-tile-title>New owner</v-list-tile-title>
                        </v-list-tile-content>
                      </v-list-tile>
                      <v-divider class="mt-2"></v-divider>
                    </template>
                  </v-autocomplete>
                </v-flex>
                <v-flex
                  xs12
                  md6
                  lg4
                  class="mb-4 pr-4"
                  v-if="!isEmpty(currentCompany.business_email)"
                >
                  <div class="dashboard__label">Business/Work Email</div>
                  <v-layout class="dashboard__value ph-no-capture" align-center>
                    {{ currentCompany.business_email }}
                  </v-layout>
                </v-flex>
                <v-flex
                  xs12
                  md6
                  lg4
                  class="mb-4 pr-4"
                  v-if="
                    !isEmpty(currentCompany.other_email) &&
                      !isIndividual(currentCompany)
                  "
                >
                  <div class="dashboard__label">Other Email</div>
                  <v-layout class="dashboard__value ph-no-capture" align-center>
                    {{ currentCompany.other_email }}
                  </v-layout>
                </v-flex>
                <v-flex
                  xs12
                  md6
                  lg4
                  class="mb-4 pr-4"
                  v-if="currentCompany.external_entity_id"
                >
                  <div class="dashboard__label">External Entity ID</div>
                  <div class="dashboard__value">
                    {{ currentCompany.external_entity_id }}
                  </div>
                </v-flex>
                <v-flex
                  xs12
                  md6
                  lg4
                  class="mb-4 pr-4"
                  v-if="currentCompany.user.external_user_id"
                >
                  <div class="dashboard__label">External User ID</div>
                  <div class="dashboard__value">
                    {{ currentCompany.user.external_user_id }}
                  </div>
                </v-flex>

                <v-flex
                  xs12
                  md6
                  lg4
                  class="mb-4 pr-4"
                  v-if="
                    currentUser.overview_setting.use_entity_tags &&
                      !isIndividual(currentCompany)
                  "
                >
                  <div class="dashboard__label">
                    Entity Tags
                    <v-tooltip bottom>
                      <v-icon
                        slot="activator"
                        class="darkgrey--text ml-1"
                        size="15"
                        style="cursor: pointer"
                        @click="
                          manageTagsDialogOpened = true;
                          manageTagType = 'entity-tags';
                        "
                      >
                        edit
                      </v-icon>
                      <span>Edit Entity Tags</span>
                    </v-tooltip>
                  </div>
                  <v-autocomplete
                    class="default-input"
                    :items="firmEntityTags"
                    v-model="entityTags"
                    placeholder="No Tags Selected"
                    solo
                    chips
                    small-chips
                    item-text="name"
                    item-value="name"
                    multiple
                    hide-details
                    @change="updateEntityTags()"
                  />
                </v-flex>

                <template v-if="spouse">
                  <v-flex xs12 md6 lg4 class="mb-4 pr-4">
                    <div class="dashboard__label">Spouse Full Name</div>
                    <div class="dashboard__value ph-no-capture">
                      {{ spouse.full_name }}
                      <v-tooltip v-if="spouse.needsAccess" bottom>
                        <v-icon
                          slot="activator"
                          color="success"
                          class="ml-1"
                          size="18"
                          style="cursor: pointer"
                        >
                          account_box
                        </v-icon>
                        <span>Spouse has dashboard access</span>
                      </v-tooltip>
                      <v-chip small v-if="spouse.is_taxpayer">Taxpayer</v-chip>
                    </div>
                  </v-flex>
                  <v-flex
                    xs12
                    md6
                    lg4
                    v-if="
                      !isEmpty(spouse.email) &&
                        !get(spouse, 'preferences.has_temp_email', false)
                    "
                    class="mb-4 pr-4"
                  >
                    <div class="dashboard__label">Spouse Email</div>
                    <div class="dashboard__value ph-no-capture">
                      {{ spouse.email }}
                    </div>
                  </v-flex>
                  <v-flex
                    xs12
                    md6
                    lg4
                    v-if="!isEmpty(spouse.work_email)"
                    class="mb-4 pr-4"
                  >
                    <div class="dashboard__label">Spouse Work Email</div>
                    <div class="dashboard__value ph-no-capture">
                      {{ spouse.work_email }}
                    </div>
                  </v-flex>
                  <v-flex
                    xs12
                    md6
                    lg4
                    v-if="!isEmpty(spouse.phone_number)"
                    class="mb-4 pr-4"
                  >
                    <div class="dashboard__label">Spouse Phone Number</div>
                    <div class="dashboard__value ph-no-capture">
                      {{ formatPhoneNumber(spouse.phone_number) }}
                    </div>
                  </v-flex>
                </template>
                <v-flex
                  xs12
                  lg8
                  class="pr-4 mb-4"
                  v-if="
                    !isEmpty(currentCompany.street) ||
                      !isEmpty(currentCompany.city) ||
                      !isEmpty(currentCompany.state) ||
                      !isEmpty(currentCompany.zip) ||
                      !isEmpty(currentCompany.country)
                  "
                >
                  <div class="dashboard__label">Address</div>
                  <div class="dashboard__value ph-no-capture">
                    {{
                      !isEmpty(currentCompany.street)
                        ? `${currentCompany.street}, `
                        : ''
                    }}{{
                      !isEmpty(currentCompany.city)
                        ? `${currentCompany.city}, `
                        : ''
                    }}{{
                      !isEmpty(currentCompany.state)
                        ? `${currentCompany.state} `
                        : ''
                    }}{{
                      !isEmpty(currentCompany.zip)
                        ? `${currentCompany.zip}`
                        : ''
                    }}{{
                      !isEmpty(currentCompany.country)
                        ? `, ${currentCompany.country}`
                        : ''
                    }}
                  </div>
                </v-flex>
              </v-layout>
            </v-layout>
          </v-layout>
        </v-card-text>
        <v-card-text v-else>
          <v-progress-circular
            indeterminate
            size="50"
            class="primary--text ma-5"
          />
        </v-card-text>
      </v-card>
      <v-card class="content-container mb-4">
        <sc-flex :gap="6" :is-y-centered="true" :is-between="true">
          <v-card-title>
            <span class="subheading font-weight-bold">{{
              !isEmpty(currentCompany.user) &&
              showEntityInformation(currentCompany)
                ? 'Primary Contact'
                : 'Client Info'
            }}</span>
            <v-tooltip
              bottom
              v-if="returnSyncEnabled && noSyncNeeded"
              class="ml-2 subheading"
            >
              <v-icon slot="activator" class="success--text">sync</v-icon>
              <span
                >Synced with Tax Software -
                {{
                  this.currentCompany.latest_update_export_task
                    ? moment(
                        this.currentCompany.latest_update_export_task
                          .updated_at,
                      ).format('LLL')
                    : 'By Import'
                }}</span
              >
            </v-tooltip>
            <v-tooltip
              bottom
              v-else-if="currentCompanyExportTaskFailed"
              @click="syncFailureDialogOpened = true"
              class="ml-2 subheading"
            >
              <v-icon slot="activator" class="error--text"
                >sync_disabled</v-icon
              >
              <span>Sync with Tax Software failed - Click to View</span>
            </v-tooltip>
            <v-tooltip
              bottom
              v-else-if="
                returnSyncEnabled && currentCompanyExportTaskReviewRequired
              "
              class="ml-2 subheading"
            >
              <v-icon
                style="cursor: pointer"
                slot="activator"
                class="warning--text"
                @click="reviewReturnDetailSyncDialogOpened = true"
                >sync_problem</v-icon
              >
              <span>Review Required for Sync - Click to Review</span>
            </v-tooltip>
            <v-tooltip
              bottom
              v-else-if="currentCompanyExportTaskScheduled"
              class="ml-2 subheading"
            >
              <v-icon slot="activator" class="warning--text"
                >access_time</v-icon
              >
              <span
                >Scheduled for data entry at
                {{
                  moment(currentCompany.latest_update_export_task.created_at)
                    .add(2, 'day')
                    .startOf('day')
                    .format('LLL')
                }}</span
              >
            </v-tooltip>
            <v-tooltip
              bottom
              v-else-if="returnSyncEnabled"
              class="ml-2 subheading"
            >
              <v-icon slot="activator" class="error--text"
                >sync_disabled</v-icon
              >
              <span
                v-if="
                  !currentCompany.meta_data ||
                    !get(currentCompany, 'meta_data.reference_id')
                "
                >Sync with Tax Software not possible - No Return ID
                specified</span
              >
              <span v-else-if="isEmpty(currentFirm.tax_softwares)"
                >Sync with Tax Software not possible - No Tax Software
                connected</span
              >
              <span v-else>Sync with Tax Software has not been completed.</span>
            </v-tooltip>
          </v-card-title>
          <sc-flex
            v-if="pageLoaded"
            :gap="6"
            :is-y-centered="true"
            :is-between="true"
          >
            <!-- <sc-label
              v-if="!isSubscribed"
              type="warning"
              icon="warning-circle"
              is-small="true"
              >Unsubscribed</sc-label
            > -->
            <soraban-action-button
              @click="$router.push(`/dashboard?companyId=${currentCompany.id}`)"
              icon="person"
              help="View as Client"
              class="success--text"
              v-if="!showEntityInformation(currentCompany)"
            />
            <soraban-action-button
              @click="openNotificationSubscriptionDialog()"
              icon="notifications_active"
              help="Notification"
              v-if="!showEntityInformation(currentCompany)"
              :filled="isSubscribed"
            />
            <soraban-action-button
              @click="internalNotesDialogOpened = true"
              icon="sticky_note_2"
              :help="`View Notes (${currentCompany.note_count})`"
              :filled="currentCompany.note_count > 0"
              v-if="!showEntityInformation(currentCompany)"
            />
            <soraban-action-button
              @click="priorYearItemsDialogOpened = true"
              icon="view_list"
              :filled="
                currentCompany && currentCompany.prior_year_items.length > 0
              "
              help="Edit Rollover Items"
              v-if="!showEntityInformation(currentCompany)"
            />
            <soraban-action-button
              @click="activityLogDialogOpened = true"
              icon="schedule"
              help="Activity Logs"
              v-if="!showEntityInformation(currentCompany)"
            />
            <v-menu
              transition="slide-y-transition"
              bottom
              right
              nudge-bottom="40"
            >
              <soraban-action-button
                slot="activator"
                icon="more_vert"
                :help="
                  showEntityInformation(currentCompany)
                    ? 'Actions'
                    : 'More Actions'
                "
              />
              <v-list>
                <v-list-tile
                  v-if="!showEntityInformation(currentCompany)"
                  @click="openEditEntityDialog()"
                >
                  <v-list-tile-title> Edit Entity Info </v-list-tile-title>
                </v-list-tile>
                <v-list-tile
                  v-else
                  @click="
                    showEntityInformation(currentCompany)
                      ? openSelectClientInfoDialog()
                      : openEditClientInfoDialog(currentCompany.user.id)
                  "
                >
                  <v-list-tile-title> Edit Client Info </v-list-tile-title>
                </v-list-tile>
                <v-list-tile @click="openSelectClientToSendLoginLinkDialog()">
                  <v-list-tile-title> Send Sign-in Link </v-list-tile-title>
                </v-list-tile>
                <v-list-tile
                  @click="switchPrimaryContactDialogOpened = true"
                  v-if="ownerIds.length > 1"
                >
                  <v-list-tile-title>
                    Change Primary Contact
                  </v-list-tile-title>
                </v-list-tile>
                <v-list-tile
                  @click="createCustomReportDialogOpened = true"
                  v-if="
                    !showEntityInformation(currentCompany) &&
                      customReportsEnabled
                  "
                >
                  <v-list-tile-title> Custom Report </v-list-tile-title>
                </v-list-tile>
                <v-list-tile
                  v-if="
                    customElementEnabled &&
                      !showEntityInformation(currentCompany)
                  "
                  @click="openCustomElementDialog()"
                >
                  <v-list-tile-title> Personalize </v-list-tile-title>
                </v-list-tile>
                <v-list-tile @click="ownerNotificationDialogOpened = true">
                  <v-list-tile-title>
                    Client Notification Setting
                  </v-list-tile-title>
                </v-list-tile>
              </v-list>
            </v-menu>
          </sc-flex>
        </sc-flex>
        <v-divider />
        <v-card-text v-if="pageLoaded">
          <v-layout>
            <v-layout justify-start align-center wrap class="text-xs-left">
              <v-flex xs12 md6 lg4 class="mb-4 pr-4">
                <div class="dashboard__label">
                  Full Name{{
                    !isEmpty(currentCompany.user.preferred_name)
                      ? ` (Preferred Name)`
                      : ''
                  }}
                </div>
                <v-layout class="dashboard__value ph-no-capture" align-center>
                  {{ currentCompany.user.full_name
                  }}{{
                    !isEmpty(currentCompany.user.preferred_name)
                      ? ` (${currentCompany.user.preferred_name})`
                      : ''
                  }}
                  <v-chip
                    v-if="
                      (isIndividual(currentCompany) &&
                        currentCompany.inactive) ||
                        !currentCompany.user.paying
                    "
                    color="error"
                    small
                    class="white--text"
                    >Inactive</v-chip
                  >
                  <v-chip
                    v-else-if="
                      (isIndividual(currentCompany) &&
                        currentCompany.readonly_access) ||
                        currentCompany.user.readonly_access
                    "
                    color="warning"
                    small
                    class="black--text"
                    >Readonly</v-chip
                  >
                  <v-chip v-if="currentCompany.user.for_testing" small
                    >Test Client</v-chip
                  >
                </v-layout>
              </v-flex>
              <v-flex xs12 md6 lg4 class="mb-4 pr-4">
                <div class="dashboard__label">Email</div>
                <div class="dashboard__value ph-no-capture">
                  <template
                    v-if="
                      get(
                        currentCompany,
                        'user.preferences.has_temp_email',
                        false,
                      )
                    "
                  >
                    <v-icon size="16" slot="activator" color="orange"
                      >warning</v-icon
                    >
                    User without email
                  </template>
                  <template v-else>
                    {{ currentCompany.user.email }}
                  </template>
                </div>
              </v-flex>
              <v-flex
                xs12
                md6
                lg4
                class="mb-4 pr-4"
                v-if="
                  !isEmpty(currentCompany.business_email) &&
                    !showEntityInformation(currentCompany)
                "
              >
                <div class="dashboard__label">Work Email</div>
                <div class="dashboard__value ph-no-capture">
                  {{ currentCompany.business_email }}
                </div>
              </v-flex>
              <v-flex
                xs12
                md6
                lg4
                class="mb-4 pr-4"
                v-if="!isEmpty(currentCompany.user.phone_number)"
              >
                <v-layout class="dashboard__label" align-center>
                  Phone Number
                </v-layout>
                <div class="dashboard__value ph-no-capture">
                  {{ formatPhoneNumber(currentCompany.user.phone_number) }}
                </div>
              </v-flex>
              <v-flex
                xs12
                md6
                lg4
                class="mb-4 pr-4"
                v-if="!isEmpty(currentCompany.user.work_phone_number)"
              >
                <v-layout class="dashboard__label" align-center>
                  Work Phone Number
                </v-layout>
                <div class="dashboard__value ph-no-capture">
                  {{ formatPhoneNumber(currentCompany.user.work_phone_number) }}
                  {{
                    !isEmpty(currentCompany.user.work_phone_extension)
                      ? `Ext. ${currentCompany.user.work_phone_extension}`
                      : ''
                  }}
                </div>
              </v-flex>
              <v-flex xs12 md6 lg4 class="mb-4 pr-4">
                <div class="dashboard__label">Time Zone</div>
                <div class="dashboard__value">
                  {{ currentCompany.user.time_zone }}
                </div>
              </v-flex>
              <v-flex
                xs12
                md6
                lg4
                class="mb-4 pr-4"
                v-if="
                  !showEntityInformation(currentCompany) &&
                    currentCompany.external_entity_id
                "
              >
                <div class="dashboard__label">External Entity ID</div>
                <div class="dashboard__value">
                  {{ currentCompany.external_entity_id }}
                </div>
              </v-flex>
              <v-flex
                xs12
                md6
                lg4
                class="mb-4 pr-4"
                v-if="currentCompany.user.external_user_id"
              >
                <div class="dashboard__label">External User ID</div>
                <div class="dashboard__value">
                  {{ currentCompany.user.external_user_id }}
                </div>
              </v-flex>

              <template v-if="!showEntityInformation(currentCompany) && spouse">
                <v-flex xs12 md6 lg4 class="mb-4 pr-4">
                  <div class="dashboard__label">Spouse Full Name</div>
                  <div class="dashboard__value ph-no-capture">
                    {{ spouse.full_name }}
                    <v-tooltip v-if="spouse.needsAccess" bottom>
                      <v-icon
                        slot="activator"
                        color="success"
                        class="ml-1"
                        size="18"
                        style="cursor: pointer"
                      >
                        account_box
                      </v-icon>
                      <span
                        >{{ spouse.is_taxpayer ? 'Taxpayer' : 'Spouse' }} has
                        dashboard access</span
                      >
                    </v-tooltip>
                    <v-chip small v-if="spouse.is_taxpayer">Taxpayer</v-chip>
                  </div>
                </v-flex>
                <v-flex
                  xs12
                  md6
                  lg4
                  v-if="
                    !isEmpty(spouse.email) &&
                      !get(spouse, 'preferences.has_temp_email', false)
                  "
                  class="mb-4 pr-4"
                >
                  <div class="dashboard__label">Spouse Email</div>
                  <div class="dashboard__value ph-no-capture">
                    {{ spouse.email }}
                  </div>
                </v-flex>
                <v-flex
                  xs12
                  md6
                  lg4
                  v-if="!isEmpty(spouse.work_email)"
                  class="mb-4 pr-4"
                >
                  <div class="dashboard__label">Spouse Work Email</div>
                  <div class="dashboard__value ph-no-capture">
                    {{ spouse.work_email }}
                  </div>
                </v-flex>
                <v-flex
                  xs12
                  md6
                  lg4
                  v-if="!isEmpty(spouse.phone_number)"
                  class="mb-4 pr-4"
                >
                  <div class="dashboard__label">Spouse Phone Number</div>
                  <div class="dashboard__value ph-no-capture">
                    {{ formatPhoneNumber(spouse.phone_number) }}
                  </div>
                </v-flex>
              </template>

              <v-flex
                xs12
                lg4
                class="mb-4 pr-4"
                v-if="!showEntityInformation(currentCompany)"
              >
                <div class="dashboard__label">Owners</div>
                <v-autocomplete
                  class="default-input ph-no-capture"
                  v-model="newOwnerIds"
                  :loading="savingOwners"
                  multiple
                  :items="allClients"
                  solo
                  hide-details
                  item-text="full_name"
                  item-value="id"
                  @blur="openConfirmNewConversationDialog()"
                >
                  <template v-slot:prepend-item>
                    <v-list-tile
                      @mousedown="addOwnerDialogOpened = true"
                      @click="openAddOwnerDialog"
                    >
                      <v-list-tile-action>
                        <v-icon>add</v-icon>
                      </v-list-tile-action>
                      <v-list-tile-content>
                        <v-list-tile-title>New owner</v-list-tile-title>
                      </v-list-tile-content>
                    </v-list-tile>
                    <v-divider class="mt-2"></v-divider>
                  </template>
                </v-autocomplete>
              </v-flex>

              <v-flex
                xs12
                md6
                lg4
                class="mb-4 pr-4"
                v-if="currentUser.overview_setting.use_contact_tags"
              >
                <div class="dashboard__label">
                  Client Tags
                  <v-tooltip bottom>
                    <v-icon
                      slot="activator"
                      class="darkgrey--text ml-1"
                      size="15"
                      style="cursor: pointer"
                      @click="
                        manageTagsDialogOpened = true;
                        manageTagType = 'client-tags';
                      "
                    >
                      edit
                    </v-icon>
                    <span>Edit Client Tags</span>
                  </v-tooltip>
                </div>
                <v-autocomplete
                  class="default-input"
                  :items="firmContactTags"
                  v-model="userContactTags"
                  placeholder="No Tags Selected"
                  solo
                  chips
                  small-chips
                  item-text="name"
                  item-value="name"
                  multiple
                  hide-details
                  @change="updateClientTags()"
                />
              </v-flex>

              <v-flex
                xs12
                md6
                lg4
                class="mb-4 pr-4"
                v-if="
                  currentUser.overview_setting.use_entity_tags &&
                    isIndividual(currentCompany)
                "
              >
                <div class="dashboard__label">
                  Entity Tags
                  <v-tooltip bottom>
                    <v-icon
                      slot="activator"
                      class="darkgrey--text ml-1"
                      size="15"
                      style="cursor: pointer"
                      @click="
                        manageTagsDialogOpened = true;
                        manageTagType = 'entity-tags';
                      "
                    >
                      edit
                    </v-icon>
                    <span>Edit Entity Tags</span>
                  </v-tooltip>
                </div>
                <v-autocomplete
                  class="default-input"
                  :items="firmEntityTags"
                  v-model="entityTags"
                  placeholder="No Tags Selected"
                  solo
                  chips
                  small-chips
                  item-text="name"
                  item-value="name"
                  multiple
                  hide-details
                  @change="updateEntityTags()"
                />
              </v-flex>

              <v-flex
                xs12
                md6
                lg4
                class="mb-4 pr-4"
                v-if="otherUserCompanies.length >= 1"
              >
                <div class="dashboard__label">Other Entities</div>
                <v-select
                  class="default-input"
                  :items="otherUserCompanies"
                  :value="currentCompanyId"
                  :placeholder="
                    otherUserCompanies.length +
                      ' other entit' +
                      (otherUserCompanies.length > 1 ? 'ies' : 'y')
                  "
                  solo
                  hide-details
                  item-text="name"
                  item-value="id"
                  @change="updateDefaultCompany"
                />
              </v-flex>
              <v-flex xs12 md6 lg4 class="mb-4 pr-4">
                <div class="dashboard__label">Assigned Firm User</div>
                <v-autocomplete
                  v-if="
                    currentUser.firm_access === 'partner' || allowAssignInGroup
                  "
                  class="default-input ph-no-capture"
                  hide-details
                  solo
                  :items="
                    filterFirmUsers(
                      currentCompany.user.assigned_advisor_id,
                      currentCompany.user.firm_group,
                    )
                  "
                  :value="currentCompany.user.assigned_advisor_id"
                  placeholder="Not Assigned"
                  item-text="name"
                  item-value="id"
                  @change="
                    newAssignedAdvisorId =>
                      updateAssignment(newAssignedAdvisorId)
                  "
                />
                <div
                  v-else-if="
                    currentCompany.user.assigned_advisor_id && firmUsers.length
                  "
                >
                  {{
                    firmUsers.find(
                      a => a.id === currentCompany.user.assigned_advisor_id,
                    ).name
                  }}
                </div>
                <div v-else class="grey--text text--darken-1">-</div>
              </v-flex>
              <v-flex xs12 md6 lg4 class="mb-4 pr-4" v-if="useFirmRoles">
                <div class="dashboard__label">Preparer</div>
                <div class="dashboard__value">{{ cpaName }}</div>
              </v-flex>
              <v-flex xs12 md6 lg4 class="mb-4 pr-4" v-if="useFirmRoles">
                <div class="dashboard__label">Client Manager</div>
                <div class="dashboard__value">{{ clientManagerName }}</div>
              </v-flex>
              <v-flex
                xs12
                md6
                lg4
                class="mb-4 pr-4"
                v-if="currentCompany.user.firm_group"
              >
                <div class="dashboard__label">Firm Group</div>
                <div class="dashboard__value">
                  {{ currentCompany.user.firm_group.name }}
                </div>
              </v-flex>
              <v-flex
                xs12
                md6
                lg8
                v-if="
                  (!showEntityInformation(currentCompany) &&
                    (!isEmpty(currentCompany.street) ||
                      !isEmpty(currentCompany.city) ||
                      !isEmpty(currentCompany.state) ||
                      !isEmpty(currentCompany.zip))) ||
                    !isEmpty(currentCompany.country)
                "
                class="mb-4 pr-4"
              >
                <div class="dashboard__label">Address</div>
                <div class="dashboard__value ph-no-capture">
                  {{
                    !isEmpty(currentCompany.street)
                      ? `${currentCompany.street}, `
                      : ''
                  }}{{
                    !isEmpty(currentCompany.city)
                      ? `${currentCompany.city}, `
                      : ''
                  }}{{
                    !isEmpty(currentCompany.state)
                      ? `${currentCompany.state} `
                      : ''
                  }}{{
                    !isEmpty(currentCompany.zip) ? `${currentCompany.zip}` : ''
                  }}{{
                    !isEmpty(currentCompany.country)
                      ? `, ${currentCompany.country}`
                      : ''
                  }}
                </div>
              </v-flex>
            </v-layout>
          </v-layout>
        </v-card-text>
        <v-card-text v-else>
          <v-progress-circular
            indeterminate
            size="50"
            class="primary--text ma-5"
          />
        </v-card-text>
      </v-card>

      <v-card class="content-container">
        <v-card-title>
          <span class="subheading font-weight-bold">{{
            projectViewEnabled ? 'Projects' : 'Questionnaires'
          }}</span>
          <v-spacer />
          <v-layout align-center justify-end>
            <template>
              <span class="mr-2">Year</span>
              <div style="width: 100px" class="mr-2">
                <v-select
                  v-model="selectedYear"
                  :items="yearOptionsWithAll"
                  hide-details
                  class="default-input body-1"
                  solo
                  small
                />
              </div>
            </template>

            <soraban-action-button
              @click="refreshQuestionnaires()"
              icon="refresh"
              help="Refresh Table"
              class="success--text"
            />
            <soraban-action-button
              @click="openSendQuestionnaireDialog()"
              icon="add"
              :disabled="!pageLoaded"
              :help="projectViewEnabled ? 'New Project' : 'New Questionnaire'"
            />
            <soraban-action-button
              @click="templateSchedulerDialogOpened = true"
              icon="date_range"
              help="Scheduler"
            />
          </v-layout>
        </v-card-title>

        <v-divider />

        <v-card-text>
          <div class="mb-4">
            <v-data-table
              :headers="questionnaireHeaders"
              :items="yearQuestionnairesMapped"
              class="project-table text-xs-left"
              :pagination.sync="questionnairePagination"
              :rows-per-page-items="questionnairePagination.options"
              :loading="tableLoading"
              :class="
                $vuetify.breakpoint.mdAndUp
                  ? 'overview-data-table'
                  : 'mobile-overview-data-table'
              "
            >
              <template slot="items" slot-scope="props">
                <tr :key="props.index" slot="header">
                  <td>
                    <v-layout align-center>
                      <div>
                        <v-tooltip bottom>
                          <a
                            slot="activator"
                            class="black--text body-1"
                            style="cursor: pointer"
                            @click="openQuestionnaireDialog(props.item)"
                            target="_self"
                          >
                            <template v-if="props.item.is_open_item">
                              {{ props.item.formatted_name }}
                            </template>
                            <template v-else>
                              {{ props.item.name }}
                            </template>
                          </a>
                          <span v-if="props.item.is_draft"> Edit draft </span>
                          <span v-else>
                            Sent on
                            {{ moment(props.item.sent_at).format('ll') }}
                          </span>
                        </v-tooltip>
                        <v-tooltip
                          bottom
                          v-if="props.item.track_changes"
                          class="ml-1"
                        >
                          <v-icon
                            slot="activator"
                            size="18"
                            class="darkgrey--text"
                          >
                            circle_notifications
                          </v-icon>
                          <span> Tracking changes </span>
                        </v-tooltip>
                        <a
                          v-if="props.item.is_draft"
                          class="font-weight-bold d-block"
                          @click="openQuestionnaireDialog(props.item)"
                        >
                          <span>(Edit draft)</span>
                        </a>
                      </div>
                    </v-layout>
                  </td>
                  <!-- Column: Progress -->
                  <td class="text-xs-left">
                    <v-tooltip bottom v-if="props.item.processing">
                      <v-layout
                        align-center
                        justify-start
                        slot="activator"
                        style="cursor: default"
                      >
                        <v-progress-circular
                          indeterminate
                          size="25"
                          color="primary"
                        ></v-progress-circular>
                      </v-layout>
                      <span> Loading </span>
                    </v-tooltip>
                    <v-layout slot="activator" class="text-xs-left" v-else>
                      <v-tooltip bottom>
                        <div slot="activator">
                          <sx-percentage-indicator
                            :total="props.item.sections.length"
                            :count="
                              props.item.sections.filter(
                                a => a.marked_completed,
                              ).length
                            "
                            :is-locked="props.item.locked"
                            :is-completed="props.item.completed"
                          >
                            <span v-if="props.item.is_draft">Draft</span>
                            <span v-else>
                              <v-layout align-center class="text-xs-left">
                                <span
                                  class="font-weight-medium"
                                  v-if="props.item.locked"
                                  >Locked</span
                                >
                                <span
                                  class="font-weight-medium"
                                  v-else-if="props.item.completed"
                                  >Completed</span
                                >
                                <span class="font-weight-medium" v-else>{{
                                  props.item.current_step
                                }}</span>
                              </v-layout>
                            </span>
                          </sx-percentage-indicator>
                        </div>
                        <v-layout column class="body-1 text-xs-left">
                          <v-flex v-if="props.item.locked">
                            Open to see details
                          </v-flex>
                          <template v-else>
                            <v-flex>
                              {{
                                props.item.opened_timestamp
                                  ? `Started ${moment(
                                      props.item.opened_timestamp,
                                    ).format('lll')}`
                                  : 'Not opened yet'
                              }}
                            </v-flex>
                            <v-flex
                              v-if="props.item.earliest_remind_date"
                              color="white"
                            >
                              Client estimated completion date:
                              {{
                                moment(
                                  moment(
                                    props.item.earliest_remind_date,
                                  ).toISOString(),
                                  moment.ISO_8601,
                                ).format('ll')
                              }}.
                            </v-flex>
                            <br />
                            <v-flex
                              v-if="
                                props.item.sections.every(
                                  a => !a.marked_completed,
                                )
                              "
                            >
                              No
                              {{ projectViewEnabled ? 'step' : 'section' }}
                              completed
                            </v-flex>
                            <v-flex
                              v-else-if="
                                props.item.sections.every(
                                  a => a.marked_completed,
                                )
                              "
                            >
                              All sections completed
                            </v-flex>
                            <v-flex v-else>
                              Incomplete Sections:
                              <v-flex
                                v-for="incompleteSection in firstThreeIncompleteSections(
                                  props.item,
                                )"
                                :key="incompleteSection.id"
                              >
                                • {{ incompleteSection.name }}
                              </v-flex>
                              <v-flex
                                v-if="
                                  props.item.sections.filter(
                                    a => !a.marked_completed,
                                  ).length > 3
                                "
                              >
                                ...
                              </v-flex>
                            </v-flex>
                          </template>
                        </v-layout>
                      </v-tooltip>
                    </v-layout>
                  </td>
                  <!-- Column: Last Activity -->
                  <td class="text-xs-center">
                    <template v-if="isLoading(props.item)">
                      <v-progress-circular
                        indeterminate
                        size="25"
                        color="primary"
                      ></v-progress-circular>
                    </template>
                    <template v-else>
                      <v-tooltip bottom>
                        <v-layout align-center slot="activator">
                          <v-flex
                            class="darkgrey--text"
                            v-if="props.item.last_activity"
                          >
                            {{ moment(props.item.last_activity).fromNow() }}
                          </v-flex>
                          <v-flex class="black--text" v-else> - </v-flex>
                        </v-layout>
                        <sx-flex :is-column="true" :gap="4">
                          <sx-tiny
                            v-if="props.item.last_activity_at"
                            color="white"
                          >
                            Last Client Logon:
                            {{ moment(props.item.last_activity_at).fromNow() }}
                          </sx-tiny>
                          <hr v-if="props.item.last_activity_at" />
                          <v-flex v-if="props.item.last_followed_up">
                            Last reminder was sent on
                            {{
                              moment(
                                moment(
                                  props.item.last_followed_up,
                                ).toISOString(),
                                moment.ISO_8601,
                              ).format('ll')
                            }}.
                          </v-flex>
                          <v-flex v-else>
                            No reminders since the sent date of
                            {{
                              moment(
                                moment(props.item.sent_at).toISOString(),
                                moment.ISO_8601,
                              ).format('ll')
                            }}.
                          </v-flex>
                          <v-flex v-if="currentFirm && !props.item.completed">
                            {{
                              getNextReminderDate(props.item, null, currentFirm)
                            }}
                          </v-flex>
                        </sx-flex>
                      </v-tooltip>
                    </template>
                  </td>
                  <td class="text-xs-center">
                    <v-layout
                      v-if="
                        props.item.locked ||
                          props.item.completed ||
                          props.item.is_draft
                      "
                    >
                      <v-flex> - </v-flex>
                    </v-layout>
                    <v-layout v-else column align-center>
                      <v-tooltip bottom v-if="pastDue(props.item.due_date)">
                        <v-layout align-center slot="activator">
                          <v-icon size="20" class="error--text mr-1"
                            >error</v-icon
                          >{{ moment(props.item.due_date).format('ll') }}
                        </v-layout>
                        <span>Past Due!</span>
                      </v-tooltip>
                      <v-tooltip
                        bottom
                        v-else-if="oneWeekAway(props.item.due_date)"
                      >
                        <v-layout align-center slot="activator">
                          <v-icon size="20" class="warning--text mr-1"
                            >warning</v-icon
                          >{{ moment(props.item.due_date).format('ll') }}
                        </v-layout>
                        <span>Less than one week left</span>
                      </v-tooltip>
                      <v-flex v-else-if="props.item.due_date">
                        {{ moment(props.item.due_date).format('ll') }}
                      </v-flex>
                      <v-flex v-else class="black--text"> - </v-flex>
                    </v-layout>
                  </td>
                  <td>
                    <v-layout align-center>
                      <v-menu
                        transition="slide-y-transition"
                        bottom
                        left
                        nudge-bottom="30"
                        class="my-1"
                      >
                        <v-icon slot="activator" color="#0a1a25">
                          more_vert
                        </v-icon>
                        <v-list>
                          <template v-if="props.item.is_draft">
                            <v-list-tile
                              @click="openQuestionnaireDialog(props.item)"
                            >
                              <v-list-tile-title>
                                Edit Draft
                              </v-list-tile-title>
                            </v-list-tile>
                          </template>
                          <template v-else>
                            <v-list-tile
                              @click="openEditQuestionnaire(props.item)"
                              v-if="!props.item.locked"
                            >
                              <v-list-tile-title>
                                Edit Reminders
                              </v-list-tile-title>
                            </v-list-tile>
                            <v-list-tile
                              v-if="reminderSendable(props.item)"
                              @click="
                                openSendQuestionnaireReminderDialog(props.item)
                              "
                            >
                              <v-list-tile-title>
                                Send Reminder
                              </v-list-tile-title>
                            </v-list-tile>
                            <v-list-tile @click="toggleLocked(props.item)">
                              <v-list-tile-title v-if="props.item.locked">
                                Unlock
                                {{
                                  projectViewEnabled
                                    ? 'Project'
                                    : 'Questionnaire'
                                }}
                              </v-list-tile-title>
                              <v-list-tile-title v-else>
                                Lock
                                {{
                                  projectViewEnabled
                                    ? 'Project'
                                    : 'Questionnaire'
                                }}
                              </v-list-tile-title>
                            </v-list-tile>
                            <v-list-tile
                              @click="openQuestionnaireExportDialog(props.item)"
                            >
                              <v-list-tile-title>
                                Export
                                {{
                                  projectViewEnabled
                                    ? 'Project'
                                    : 'Questionnaire'
                                }}
                              </v-list-tile-title>
                            </v-list-tile>
                            <v-list-tile
                              @click="generateQuestionnaireLink(props.item.id)"
                            >
                              <v-list-tile-title>
                                Copy
                                {{
                                  projectViewEnabled
                                    ? 'Project'
                                    : 'Questionnaire'
                                }}
                                Link
                              </v-list-tile-title>
                            </v-list-tile>
                          </template>
                          <v-list-tile
                            @click="openRemoveQuestionnaireDialog(props.item)"
                          >
                            <v-list-tile-title> Remove </v-list-tile-title>
                          </v-list-tile>
                        </v-list>
                      </v-menu>
                    </v-layout>
                  </td>
                </tr>
              </template>
            </v-data-table>
          </div>
        </v-card-text>
      </v-card>

      <signature-request-table
        v-if="signatureRequestsEnabled"
        :key="currentCompany.id"
        :currentCompany="currentCompany"
        :selectedYear="selectedYear"
      />

      <tax-return-table
        v-if="newTaxReturnDeliveryEnabled"
        :key="currentCompany.id"
        :currentCompany="currentCompany"
        :initialYear="selectedYear"
      />
    </v-container>
  </v-content>
</template>

<script>
import moment from 'moment';
import API from '@/api';
import { mapState, mapGetters } from 'vuex';
import isEmpty from 'lodash/isEmpty';
import sortBy from 'lodash/sortBy';
import get from 'lodash/get';
import uniq from 'lodash/uniq';
import ActivityLog from '@/components/ActivityLog';
import InternalNote from '@/components/InternalNote';
import ConfirmationDialog from '@/components/ConfirmationDialog';
import SmartQuestionnaireView from '@/components/SmartQuestionnaireView';
import ExportQuestionnaireDialog from '@/components/ExportQuestionnaireDialog';
import SwitchPrimaryContactDialog from '@/components/dialogs/SwitchPrimaryContactDialog';
import SendTemplateDialog from '@/components/dialogs/SendTemplateDialog';
import SendQuestionnaireDialog from '@/components/dialogs/SendQuestionnaireDialog';
import SendOpenItemsDialog from '@/components/dialogs/SendOpenItemsDialog';
import SendDocumentDropOffDialog from '@/components/dialogs/SendDocumentDropOffDialog';
import TemplateSchedulerDialog from '@/components/dialogs/TemplateSchedulerDialog';
import CreateCustomReportDialog from '@/components/dialogs/CreateCustomReportDialog';
import SendQuestionnaireReminderDialog from '@/components/dialogs/SendQuestionnaireReminderDialog';
import NotificationSubscriptionDialog from '@/components/dialogs/NotificationSubscriptionDialog';
import EditClientDialog from '@/components/dialogs/EditClientDialog';
import EditEntityDialog from '@/components/dialogs/EditEntityDialog';
import ImportPriorYearItemsDialog from '@/components/dialogs/ImportPriorYearItemsDialog';
import ManageTagsDialog from '@/components/dialogs/ManageTagsDialog';
import PriorYearItems from '@/components/PriorYearItems';
import SignatureRequestTable from '@/components/tables/SignatureRequestTable';
import SxTiny from '@/components/sx/SxTiny';
import SxFlex from '@/components/sx/SxFlex';
import SxPercentageIndicator from '@/components/sx/SxPercentageIndicator';
import TaxReturnTable from '@/components/returns/TaxReturnTable';
import ReviewReturnDetailSyncDialog from '@/components/dialogs/ReviewReturnDetailSyncDialog';
import OwnerNotificationDialog from '@/components/dialogs/OwnerNotificationDialog';
import ConfirmNewConversationDialog from '@/components/dialogs/ConfirmNewConversationDialog';
import { compareObjects } from '@/utils/objects';
import {
  states,
  otherCountries,
  timeZones,
  defaultEntityTypes,
  rules,
  yearOptions,
} from '@/constants/variables';
import GlobalUiAlert from '../../components/GlobalUiAlert';
import UtilsMixins from '@/mixins/UtilsMixins';
import FirmPreferenceMixins from '@/mixins/FirmPreferenceMixins';
import AdminFunctionsMixin from '@/mixins/AdminFunctionsMixin';
import QuestionnaireMixin from '../../mixins/QuestionnaireMixin';
import ErrorMessageDialog from '@/components/dialogs/ErrorMessageDialog';

export default {
  name: 'AdminDashboard',
  components: {
    GlobalUiAlert,
    ActivityLog,
    ManageTagsDialog,
    InternalNote,
    SmartQuestionnaireView,
    ExportQuestionnaireDialog,
    EditEntityDialog,
    SwitchPrimaryContactDialog,
    SendTemplateDialog,
    SendQuestionnaireDialog,
    SendOpenItemsDialog,
    SendDocumentDropOffDialog,
    ConfirmationDialog,
    TemplateSchedulerDialog,
    CreateCustomReportDialog,
    SendQuestionnaireReminderDialog,
    EditClientDialog,
    NotificationSubscriptionDialog,
    PriorYearItems,
    ImportPriorYearItemsDialog,
    SignatureRequestTable,
    SxTiny,
    SxFlex,
    SxPercentageIndicator,
    ErrorMessageDialog,
    TaxReturnTable,
    ReviewReturnDetailSyncDialog,
    OwnerNotificationDialog,
    ConfirmNewConversationDialog,
  },
  mixins: [
    UtilsMixins,
    AdminFunctionsMixin,
    FirmPreferenceMixins,
    QuestionnaireMixin,
  ],
  data() {
    return {
      taxSoftwares: ['UltraTax', 'ProConnect', 'ProSeries', 'Drake', 'Lacerte'],
      groupSelectErrorMessage: [],
      selectedGroupId: null,
      selectedTaxSoftware: null,
      previousTitle: '',
      savingQuestionnaire: false,
      timeZones,
      otherCountries,
      states,
      rules,
      yearOptions,
      smartQuestionnaireViewDefaultTab: null,
      remindersDisabled: false,
      savingOwners: false,
      reportTemplates: [],
      sendLoginLinkConfirmationDialogOpened: false,
      selectClientToSendLoginLinkDialogOpened: false,
      createCustomReportDialogOpened: false,
      selectedOpenItemQuestionnaire: null,
      lockQuestionnaireConfirmationDialogOpened: false,
      lockQuestionnaireAfterExportConfirmationDialogOpened: false,
      sendQuestionnaireReminderDialogOpened: false,
      priorYearItemsDialogOpened: false,
      internalNotesDialogOpened: false,
      switchPrimaryContactDialogOpened: false,
      sendQuestionnaireDialogOpened: false,
      sendOpenItemsDialogOpened: false,
      sendTemplateDialogOpened: false,
      sendDocumentDropOffDialogOpened: false,
      customElementDialogOpened: false,
      activeTab: 'general',
      pageLoaded: false,
      manageTagsDialogOpened: false,
      manageTagType: 'client-tags',
      activityLogTypeFilter: null,
      spouse: null,
      activitiesLogTypes: [
        {
          value: 0,
          text: 'Uncategorized',
        },
        {
          value: 1,
          text: 'Detail change',
        },
        {
          value: 2,
          text: 'Document related',
        },
        {
          value: 3,
          text: 'Questionnaire related',
        },
        {
          value: 4,
          text: 'Conversation related',
        },
        {
          value: 5,
          text: 'Tax return delivery related',
        },
        {
          value: 6,
          text: 'Review and export related',
        },
      ],
      preferredContactMethods: [
        {
          label: 'Email & Text',
          value: 'sms',
        },
        {
          label: 'Email',
          value: 'email',
        },
      ],
      hasSpouse: false,
      newTypes: defaultEntityTypes,
      yearQuestionnaires: [],
      userContactTags: [],
      entityTags: [],
      entitySaving: false,
      addOwnerDialogOpened: false,
      questionnaireHeaders: [
        {
          text: 'Name (Click to view)',
          align: 'left',
          sortable: true,
          value: 'name',
          width: '30%',
        },
        {
          text: 'Status',
          align: 'left',
          sortable: true,
          value: 'status',
          width: '25%',
        },
        {
          text: 'Last Activity',
          align: 'center',
          sortable: true,
          value: 'last_activity',
          width: '15%',
        },
        {
          text: 'Due Date',
          align: 'center',
          sortable: true,
          value: 'due_date',
          width: '20%',
        },
        {
          text: 'Actions',
          align: 'center',
          sortable: false,
          width: '7%',
        },
      ],
      firmUsers: [],
      questionnairePagination: {
        sortBy: 'last_activity',
        rowsPerPage: 5,
        descending: true,
        page: 1,
        options: [
          {
            text: '5',
            value: 5,
          },
          {
            text: '10',
            value: 10,
          },
          {
            text: '20',
            value: 20,
          },
          {
            text: 'All',
            value: -1,
          },
        ],
      },
      exportQuestionnaireDialogOpened: false,
      notificationSubscriptionDialogOpened: false,
      selectedYear: null,
      exporting: false,
      questionnaireDialogOpened: false,
      commentDialogOpened: false,
      selectedQuestionnaire: null,
      questionnaireTemplates: [],
      sectionsToInclude: [],
      engagementLetterIncluded: true,
      removeQuestionnaireConfirmationDialogOpened: false,
      editClientInfoDialogOpened: false,
      followUpDialogOpened: false,
      activityLogDialogOpened: false,
      seeAllActivities: false,
      editEntityInfoDialogOpened: false,
      questionsToCreate: [
        {
          text: '',
          section: 'Question',
          question_type: 'upload',
          options: [],
          newOption: '',
          advanced: false,
        },
      ],
      newOption: '',
      questionTypes: [
        {
          text: 'Upload',
          value: 'upload',
        },
        {
          text: 'Short Answer',
          value: 'text',
        },
      ],
      entityToSave: null,
      clientToSave: null,
      currentPath: '/_soraban',
      tableLoading: true,
      questionnaireDueDate: null,
      questionnaireYear: null,
      editDueDateDialogOpened: false,
      questionnaireLoading: false,
      openItemsName: '',
      openItemsPreview: false,
      selectedQuestion: null,
      newTemplateScheduler: false,
      templateSchedulerDialogOpened: false,
      schedulerDialogOpened: false,
      newOwnerIds: [],
      newOwnerFirstName: '',
      newOwnerLastName: '',
      newOwnerEmail: '',
      newOwnerPhoneNumber: '',
      newItems: '',
      documentsOptional: false,
      signRequiredByAllOwner: false,
      rollOverYear: 2024,
      rollOverYearOptions: yearOptions.map(year => year + 1),
      suggestionsLoading: false,
      sendDocumentRequestDialogOpened: false,
      newDocumentRequestDaysUntilDue: 7,
      suggestedDocumentRequests: [],
      refreshTries: 0,
      allClients: [],
      allQuestionnaireYears: [],
      selectedQuestionnaires: [],
      currentCompanyId: null,
      currentCompany: {
        prior_year_items: [],
        owner_ids_without_spouse: [],
        owner_ids: [],
        notification_subscribed_users: [],
        user: {},
      },
      selectClientToEditDialogOpened: false,
      selectedOwnerId: null,
      childQuestionnaire: null,
      errorMessageDialogOpened: false,
      errorMessage: '',
      reviewReturnDetailSyncDialogOpened: false,
      ownerNotificationDialogOpened: false,
      confirmNewConversationDialogOpened: false,
      comingFromAddOwner: false,
    };
  },
  computed: {
    ...mapState('companies', ['companies']),
    ...mapGetters({
      currentUser: 'user',
      isAdvisor: 'isAdvisor',
      notifications: 'notifications',
      defaultCompany: 'defaultCompany',
      currentFirm: 'currentFirm',
    }),
    noSyncNeeded() {
      return (
        this.currentCompanyExportTaskCompleted ||
        this.currentCompanyExportTaskEmpty
      );
    },
    currentCompanyExportTaskScheduled() {
      return (
        this.currentCompany.latest_update_export_task &&
        this.currentCompany.latest_update_export_task.status === 'scheduled'
      );
    },
    currentCompanyExportTaskReviewRequired() {
      return (
        this.currentCompany.latest_update_export_task &&
        this.currentCompany.latest_update_export_task.status ===
          'review_required'
      );
    },
    currentCompanyExportTaskFailed() {
      return (
        this.currentCompany.latest_update_export_task &&
        this.currentCompany.latest_update_export_task.status === 'failed'
      );
    },
    currentCompanyExportTaskCompleted() {
      return (
        this.currentCompany.latest_update_export_task &&
        this.currentCompany.latest_update_export_task.status === 'completed'
      );
    },
    currentCompanyExportTaskEmpty() {
      return isEmpty(this.currentCompany.latest_update_export_task);
    },
    newTypesWithoutIndividual() {
      return this.newTypes.filter(type => type !== 'Individual');
    },
    allowAssignInGroup() {
      return this.currentUser.firm_access === 'group' && this.hasFirmGroups;
    },
    clientManagerName() {
      let manager = this.currentCompany.user.client_manager_id
        ? this.firmUsers.find(
            a => a.id === this.currentCompany.user.client_manager_id,
          )
        : null;
      if (manager) {
        return manager.name;
      } else {
        return '-';
      }
    },
    cpaName() {
      let cpa = this.currentCompany.user.cpa_id
        ? this.firmUsers.find(a => a.id === this.currentCompany.user.cpa_id)
        : null;
      if (cpa) {
        return cpa.name;
      } else {
        return '-';
      }
    },
    firmContactTags() {
      if (
        !isEmpty(this.currentFirm.contact_tags) &&
        this.currentCompany.user.contact_tags
      ) {
        return this.currentFirm.contact_tags
          .concat(...this.currentCompany.user.contact_tags)
          .filter(this.onlyUnique);
      } else {
        return [];
      }
    },
    firmEntityTags() {
      if (
        !isEmpty(this.currentFirm.entity_tags) &&
        this.currentCompany.entity_tags
      ) {
        return this.currentFirm.entity_tags
          .concat(...this.currentCompany.entity_tags)
          .filter(this.onlyUnique);
      } else {
        return [];
      }
    },
    yearQuestionnairesMapped() {
      return this.yearQuestionnaires.map(questionnaire => ({
        name: questionnaire.name,
        ...this.objectWithoutProperties(questionnaire, ['name']),
      }));
    },
    isSubscribed() {
      return this.currentCompany.notification_subscribed_users.includes(
        this.currentUser.id,
      );
    },
    clientSavable() {
      return (
        this.clientToSave.email &&
        this.clientToSave.fname &&
        this.clientToSave.lname
      );
    },
    otherUserCompanies() {
      if (this.currentCompany.user.is_advisor) {
        return this.companies.filter(c => {
          return (
            c.user.id === this.currentCompany.user.id &&
            c.id !== this.currentCompanyId
          );
        });
      } else {
        return this.companies.filter(c => {
          return (
            this.currentCompany.user.owned_company_ids.includes(c.id) &&
            c.id !== this.currentCompanyId
          );
        });
      }
    },
    questionnaireSavable() {
      return (
        !this.selectedQuestionnaire.due_date ||
        moment() < moment(this.selectedQuestionnaire.due_date)
      );
    },
    ownerOptions() {
      return this.allClients.filter(
        u =>
          this.currentCompany.owner_ids.includes(u.id) &&
          this.currentCompany.user.id !== u.id,
      );
    },
    ownerIds() {
      if (this.currentCompany) {
        return this.currentCompany.owner_ids;
      }
    },
    yearOptionsFromPastQuestionnaires() {
      return uniq(this.yearOptions.concat(this.allQuestionnaireYears));
    },
    yearOptionsWithAll() {
      return [
        'All',
        ...this.yearOptionsFromPastQuestionnaires.map(c => c.toString()),
      ];
    },
    isPartnerOrFullUser() {
      return ['partner', 'full'].includes(this.currentUser.firm_access);
    },
  },
  methods: {
    isEmpty,
    get,
    moment,

    openSendQuestionnaireDialog() {
      if (this.currentCompany.inactive) {
        this.errorMessageDialogOpened = true;
        this.errorMessage =
          'Unable to send questionnaires to an inactive entity. Please reactivate the entity first.';
      } else {
        this.sendQuestionnaireDialogOpened = true;
      }
    },

    async generateQuestionnaireLink(id) {
      const response = await API.questionnaire.generateQuestionnaireLink({
        id,
        companyId: this.currentCompany.id,
      });
      this.copyTextToClipboard(response.data.link);
      this.$store.dispatch('application/setSuccessNotification', {
        message: 'Questionnaire link was copied to clipboard.',
      });
    },
    viewParentQuestionnaire() {
      this.$router.push(
        `/questionnaire?companyId=${this.selectedQuestionnaire.company_id}&questionnaireId=${this.selectedQuestionnaire.id}`,
      );
    },
    openNotificationSubscriptionDialog() {
      this.notificationSubscriptionDialogOpened = true;
    },
    async sendClientSignInLink(user_id) {
      try {
        const response = await API.advisor.sendClientLoginInfo({
          userIds: [user_id],
        });
        if (response.data.error) {
          console.log(response.data.error);
          this.$store.dispatch('application/setErrorNotification', {
            message: response.data.error,
            errorToSend: response.data.error,
            userEmail: this.currentUser.email,
          });
        } else {
          this.$store.dispatch('application/setSuccessNotification', {
            message: 'Sent sign-in link!',
          });
          this.sendLoginLinkConfirmationDialogOpened = false;
          this.selectClientToSendLoginLinkDialogOpened = false;
        }
      } catch (e) {
        console.log(e);
      }
    },
    onNotificationSubscriptionRefresh(subscriptions) {
      const isUnsubscribed = !subscriptions || !subscriptions.length;
      const newNotificationSubscribedUsers = isUnsubscribed
        ? this.currentCompany.notification_subscribed_users.filter(
            u => u !== this.currentUser.id,
          )
        : [
            ...this.currentCompany.notification_subscribed_users,
            this.currentUser.id,
          ];

      this.currentCompany = {
        ...this.currentCompany,
        notification_subscribed_users: newNotificationSubscribedUsers,
      };
    },
    objectWithoutProperties(obj, keys) {
      var target = {};
      for (var i in obj) {
        if (keys.indexOf(i) >= 0) continue;
        if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
        target[i] = obj[i];
      }
      return target;
    },
    reminderSendable(questionnaire) {
      return !questionnaire.completed && !questionnaire.locked;
    },
    openSendQuestionnaireReminderDialog(questionnaire) {
      if (this.currentCompany.inactive) {
        this.errorMessageDialogOpened = true;
        this.errorMessage =
          'Unable to send reminders to an inactive entity. Please reactivate the entity first.';
      } else {
        this.selectedQuestionnaires = [questionnaire];
        this.sendQuestionnaireReminderDialogOpened = true;
      }
    },
    async getAllQuestionnaireYears() {
      const response = await API.advisor.getAllQuestionnaireYears();
      this.allQuestionnaireYears = response.data.years;
    },
    showEntityInformation(entity) {
      return (
        entity.entity_type !== 'Individual' ||
        this.otherUserCompanies.filter(c => c.entity_type === 'Individual')
          .length
      );
    },
    openQuestionnaireExportDialog(questionnaire) {
      this.questionnaireDialogOpened = false;
      this.selectedQuestionnaire = questionnaire;
      this.exportQuestionnaireDialogOpened = true;
    },
    async getAllClients() {
      const response = await API.firm.getAllClients();
      this.allClients = sortBy(response.data.users, ['full_name']);
    },
    filterFirmUsers(assignedId, clientGroup) {
      if (this.isPartnerOrFullUser) {
        // Full, partners or Users in clients group
        return this.firmUsers.filter(user => {
          return (
            ['partner', 'full', 'basic'].includes(user.access) ||
            (user.group && clientGroup && user.group.id === clientGroup.id)
          );
        });
      } else {
        // Assigned user or in same group
        return this.firmUsers.filter(user => {
          return (
            (assignedId && user.id === assignedId) ||
            (user.group && user.group.id === this.currentUser.firm_group.id)
          );
        });
      }
    },
    async updateAssignment(id) {
      try {
        const previouslyAssignedAdvisorId = this.currentCompany.user
          .assigned_advisor_id;
        const response = await API.advisor.updateAssignments({
          ids: [this.currentCompany.user.id],
          advisorId: id,
        });
        if (response.data.error) {
          this.$store.dispatch('application/setErrorNotification', {
            message: response.data.error,
          });
        } else {
          await this.sleep(2000);
          this.getCurrentCompany();
          this.$store.dispatch('application/setSuccessNotification', {
            message: `The newly assigned firm user has been added to all of this client's conversations, and replaced ${
              this.firmUsers.find(u => u.id === previouslyAssignedAdvisorId)
                .name
            } in the personal conversation.`,
          });
        }
        this.$store.dispatch('companies/getCompanies');
      } catch (e) {
        console.log(e);
      }
    },
    async getAllFirmUsers() {
      const response = await API.firm.getAllUsers();
      if (response.data.error) {
        console.log(response.data.error);
      } else {
        this.firmUsers = response.data.users;
      }
    },
    async updateClientTags() {
      try {
        const response = await API.user.updateTags({
          userId: this.currentCompany.user.id,
          contactTags: this.userContactTags,
        });
        if (response.data.error) {
          console.log(response.data.error);
          this.$store.dispatch('application/setErrorNotification', {
            message: response.data.error,
          });
        } else {
          this.$store.dispatch('application/setSuccessNotification', {
            message: 'Successfully saved.',
          });
          await this.getCurrentCompany();
        }
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Something went wrong while saving tags.',
        });
      }
    },
    async updateEntityTags() {
      try {
        const response = await API.company.updateTags({
          companyId: this.currentCompany.id,
          entityTags: this.entityTags,
        });
        if (response.data.error) {
          console.log(response.data.error);
          this.$store.dispatch('application/setErrorNotification', {
            message: response.data.error,
          });
        } else {
          this.$store.dispatch('application/setSuccessNotification', {
            message: 'Successfully saved.',
          });
          await this.getCurrentCompany();
        }
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Something went wrong while saving tags.',
        });
      }
    },
    async getAllEntityTypes() {
      const response = await API.firm.getAllEntityTypes();
      this.newTypes = uniq(
        defaultEntityTypes.concat(response.data.entity_types),
      );
    },
    createTemplateScheduler() {
      this.newTemplateScheduler = true;
      this.sendTemplateDialogOpened = true;
      this.templateSchedulerDialogOpened = false;
    },
    openEditQuestionnaire(questionnaire) {
      this.selectedQuestionnaire = Object.assign({}, questionnaire);
      this.remindersDisabled = this.selectedQuestionnaire.reminders_disabled;
      this.editDueDateDialogOpened = true;
    },
    isIndividual(entity) {
      return entity.entity_type === 'Individual';
    },
    editOpenItems(questionnaire) {
      this.selectedQuestionnaire = Object.assign({}, questionnaire);
      this.selectedOpenItemQuestionnaire = Object.assign({}, questionnaire);
      this.sendOpenItemsDialogOpened = true;
    },
    async openQuestionnaireDialog(questionnaire, defaultTab) {
      if (questionnaire.processing) {
        this.$store.dispatch('application/setSuccessNotification', {
          message: 'Please wait until the questionnaire is created.',
        });
      } else {
        this.smartQuestionnaireViewDefaultTab = null;
        if (questionnaire.is_draft && questionnaire.is_open_items) {
          this.editOpenItems(questionnaire);
        } else {
          if (defaultTab) {
            this.smartQuestionnaireViewDefaultTab = defaultTab;
          }
          this.questionnaireDialogOpened = true;
          this.selectedQuestionnaire = questionnaire;
          this.questionnaireLoading = true;
          const response = await API.questionnaire.getQuestionnaire({
            companyId: questionnaire.company_id,
            id: questionnaire.id,
          });
          this.selectedQuestionnaire = response.data;

          this.questionnaireLoading = false;
        }
      }
    },
    async refreshQuestionnaireDialog(questionnaire, done) {
      try {
        if (questionnaire.is_draft && questionnaire.is_open_items) {
          this.editOpenItems(questionnaire);
        } else {
          this.selectedQuestionnaire = questionnaire;
          const response = await API.questionnaire.getQuestionnaire({
            companyId: questionnaire.company_id,
            id: questionnaire.id,
          });
          this.selectedQuestionnaire = response.data;
          this.questionnaireLoading = false;

          if (done) {
            done();
          }
        }
      } catch (e) {
        console.log(e);
      }
    },
    openEditEntityDialog() {
      this.entityToSave = Object.assign({}, this.currentCompany);
      this.clientToSave = Object.assign({}, this.currentCompany.user);
      this.editEntityInfoDialogOpened = true;
    },
    openSelectClientInfoDialog() {
      this.clientToSave = null;
      this.selectedOwnerId = null;
      if (this.currentCompany.owner_ids_without_spouse.length === 1) {
        this.clientToSave = Object.assign({}, this.currentCompany.user);
        this.editClientInfoDialogOpened = true;
      } else {
        this.selectClientToEditDialogOpened = true;
      }
    },
    openSelectClientToSendLoginLinkDialog() {
      if (this.currentCompany.owners.length === 1) {
        this.sendLoginLinkConfirmationDialogOpened = true;
      } else {
        this.selectedOwnerId = null;
        this.selectClientToSendLoginLinkDialogOpened = true;
      }
    },
    openCustomElementDialog() {
      this.entityToSave = Object.assign({}, this.currentCompany);
      this.clientToSave = Object.assign({}, this.currentCompany.user);
      this.customElementDialogOpened = true;
    },
    openEditClientInfoDialog(ownerId) {
      this.selectClientToEditDialogOpened = false;
      this.clientToSave = Object.assign(
        {},
        this.currentCompany.owners.find(u => u.id === ownerId),
      );
      this.editClientInfoDialogOpened = true;
    },
    async saveEntity() {
      // Move this block, it's only used for custom element
      this.entitySaving = true;
      if (!this.showEntityInformation(this.entityToSave)) {
        this.entityToSave.name =
          this.clientToSave.lname + ', ' + this.clientToSave.fname;
      }
      const {
        name,
        entity_type,
        street,
        city,
        state,
        zip,
        business_email,
        other_email,
        fax,
        custom_element,
      } = this.entityToSave;
      const {
        fname,
        lname,
        email,
        preferred_name,
        preferred_contact_method,
        passwordless,
        phone_number,
        paying,
        time_zone,
        work_phone_number,
        work_phone_extension,
        cpa_id,
        client_manager_id,
      } = this.clientToSave;
      try {
        const response = await API.company.updateCompany({
          id: this.currentCompanyId,
          userId: this.currentCompany.user.id,
          name,
          fname,
          lname,
          email,
          preferred_name,
          phone_number,
          work_phone_number,
          work_phone_extension,
          entity_type,
          street,
          city,
          paying,
          state,
          zip,
          time_zone,
          passwordless,
          business_email,
          other_email,
          fax,
          cpa_id,
          client_manager_id,
          custom_element,
          preferred_contact_method,
        });
        if (response.data.error) {
          console.log(response.data.error);
          this.$store.dispatch('application/setErrorNotification', {
            message: 'Something went wrong while saving information.',
          });
        } else {
          await this.getCurrentCompany();
          this.$store.dispatch('application/setSuccessNotification', {
            message: `Information has been saved successfully`,
          });
          this.editEntityInfoDialogOpened = false;
          this.customElementDialogOpened = false;
          this.clientToSave = null;
          this.entityToSave = null;
          await this.$store.dispatch('companies/getCompanies');
        }
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Something went wrong while saving information.',
        });
      } finally {
        this.entitySaving = false;
      }
    },
    async updateDefaultCompany(companyId) {
      const companyObject = this.otherUserCompanies.find(
        company => company.id === companyId,
      );

      this.$router.push({
        name: this.$router.currentRoute.name,
        params: {
          hash:
            companyObject.name.replaceAll(' ', '-').replaceAll(',', '') +
            '-' +
            companyObject.hash_id,
        },
      });
    },
    async getSpouse() {
      const spouse = await API.company.getSpouse(this.currentCompanyId);
      this.spouse = spouse.data;
    },
    async getCurrentCompany() {
      await this.$store.dispatch('setDefaultCompany', {
        companyId: this.currentCompanyId,
      });
      const response = await API.company.getCompany(this.currentCompanyId);
      this.$set(this, 'currentCompany', response.data);
      this.newOwnerIds = this.currentCompany.owner_ids_without_spouse;
      await this.getAllClients();
      await this.getSpouse();
    },
    ownersAreChangingAndReadyToChange() {
      if (this.comingFromAddOwner) return true;

      // Validate incoming data
      const isValid =
        !this.savingOwners &&
        this.newOwnerIds &&
        this.currentCompany &&
        this.currentCompany.owner_ids_without_spouse;

      if (!isValid) return false;

      return !compareObjects(
        this.newOwnerIds,
        this.currentCompany.owner_ids_without_spouse,
      );
    },
    openConfirmNewConversationDialog() {
      if (this.addOwnerDialogOpened) return;

      if (this.ownersAreChangingAndReadyToChange()) {
        this.confirmNewConversationDialogOpened = true;
      }
    },
    async updateOwners(
      shouldCopyOverPreviousMessages = false,
      shouldTransferOverPreviousMessages = false,
    ) {
      this.confirmNewConversationDialogOpened = false;

      if (this.comingFromAddOwner) {
        return this.addOwner(
          shouldCopyOverPreviousMessages,
          shouldTransferOverPreviousMessages,
        );
      }

      if (this.ownersAreChangingAndReadyToChange()) {
        this.savingOwners = true;
        setTimeout(async () => {
          // Prevent duplicates calls
          if (isEmpty(this.newOwnerIds)) {
            this.$store.dispatch('application/setErrorNotification', {
              message: 'You cannot get rid of all owners.',
            });
            this.newOwnerIds = this.currentCompany.owner_ids_without_spouse;
          } else {
            try {
              const response = await API.company.updateOwners({
                companyId: this.currentCompanyId,
                ownerIds: this.newOwnerIds,
                shouldCopyOverPreviousMessages,
                shouldTransferOverPreviousMessages,
              });

              await this.getCurrentCompany();
              this.newOwnerIds = this.currentCompany.owner_ids_without_spouse;
              if (response.data.error) {
                console.log(response.data.error);
                this.$store.dispatch('application/setErrorNotification', {
                  message: response.data.error,
                });
              } else {
                this.$store.dispatch('application/setSuccessNotification', {
                  message: 'Successfully saved.',
                });
              }
            } catch (e) {
              console.log(e);
              this.$store.dispatch('application/setErrorNotification', {
                message: 'Something went wrong while saving information.',
              });
              this.newOwnerIds = this.currentCompany.owner_ids_without_spouse;
            }
          }
          this.savingOwners = false;
        }, 300);
      }
    },
    async saveRemindersDisabled() {
      this.savingQuestionnaire = true;
      const response = await API.questionnaire.saveQuestionnaire({
        company_id: this.selectedQuestionnaire.company_id,
        id: this.selectedQuestionnaire.id,
        due_date: this.selectedQuestionnaire.due_date,
        reminders_disabled: this.remindersDisabled,
      });
      if (response.data.error) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Something went wrong.',
          errorToSend: e,
          userEmail: this.currentUser.email,
        });
      } else {
        this.$store.dispatch('application/setSuccessNotification', {
          message: 'Updated.',
        });
        this.editDueDateDialogOpened = false;
        this.refreshQuestionnaires();
      }
      this.savingQuestionnaire = false;
    },
    async saveQuestionnaire() {
      this.savingQuestionnaire = true;
      const response = await API.questionnaire.saveQuestionnaire({
        company_id: this.currentCompanyId,
        id: this.selectedQuestionnaire.id,
        due_date: this.selectedQuestionnaire.due_date,
        reminders_disabled: this.selectedQuestionnaire.reminders_disabled,
      });
      if (response.data.error) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Something went wrong.',
          errorToSend: e,
          userEmail: this.currentUser.email,
        });
      } else {
        this.$store.dispatch('application/setSuccessNotification', {
          message: 'Updated.',
        });
        this.editDueDateDialogOpened = false;
        this.refreshQuestionnaires();
      }
      this.savingQuestionnaire = false;
    },
    async toggleTrackChanges(trackChange) {
      const response = await API.questionnaire.saveQuestionnaire({
        company_id: this.selectedQuestionnaire.company_id,
        id: this.selectedQuestionnaire.id,
        track_changes: trackChange,
      });
      this.selectedQuestionnaire.track_changes = !this.selectedQuestionnaire
        .track_changes;
      this.refreshQuestionnaires();
      this.lockQuestionnaireAfterExportConfirmationDialogOpened = false;
      this.$forceUpdate();
    },
    async toggleLocked(questionnaire) {
      const response = await API.questionnaire.saveQuestionnaire({
        company_id: questionnaire.company_id,
        id: questionnaire.id,
        locked: !questionnaire.locked,
      });
      if (response.data.error) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Something went wrong.',
          errorToSend: e,
          userEmail: this.currentUser.email,
        });
      } else {
        if (questionnaire.locked) {
          this.$store.dispatch('application/setSuccessNotification', {
            message: "You've successfully unlocked the questionnaire.",
          });
        } else {
          this.$store.dispatch('application/setSuccessNotification', {
            message: "You've successfully locked the questionnaire.",
          });
        }
        this.refreshQuestionnaires();
        questionnaire.locked = !questionnaire.locked;
        this.lockQuestionnaireConfirmationDialogOpened = false;
        this.lockQuestionnaireAfterExportConfirmationDialogOpened = false;
      }
    },
    openLockQuestionnaireConfirmationDialog() {
      this.lockQuestionnaireAfterExportConfirmationDialogOpened = true;
    },
    isLoading(questionnaire) {
      let date = moment(questionnaire.created_at);
      return date.add(5, 'seconds') > moment() || questionnaire.processing;
    },
    async sendFollowUp() {
      const response = await API.questionnaire.sendQuestionnaireFollowUp({
        companyId: this.currentCompanyId,
        questionnaireId: this.selectedQuestionnaire.id,
      });
      if (response.data.error) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'Something went wrong while sending the follow up.',
          errorToSend: e,
          userEmail: this.currentUser.email,
        });
      } else {
        this.refreshQuestionnaires();
        this.followUpDialogOpened = false;
        this.$store.dispatch('application/setSuccessNotification', {
          message: 'Reminder Sent!',
        });
        this.$forceUpdate();
      }
    },
    listDocuments(answer) {
      return `Uploaded: ${answer
        .split('&!&')
        .slice(1)
        .map(a => a.split('/')[a.split('/').length - 1])
        .join(', ')}`;
    },
    getUser(user_id) {
      let user = this.companies.map(a => a.user).find(u => u.id === user_id);
      if (user) {
        return user.full_name;
      }
      return '';
    },
    getFileType(documentSource) {
      const splitSource = documentSource.split('.');
      return splitSource[splitSource.length - 1];
    },
    openLink(link) {
      window.open(link, '_blank');
    },
    openRemoveQuestionnaireDialog(questionnaire) {
      this.selectedQuestionnaire = questionnaire;
      if (
        this.selectedQuestionnaire.answered_questions !== 0 ||
        this.selectedQuestionnaire.engagement_letter_signature
      ) {
        this.removeQuestionnaireConfirmationDialogOpened = true;
      } else {
        this.deleteQuestionnaire();
      }
    },
    async deleteQuestionnaire() {
      const response = await API.questionnaire.deleteQuestionnaire({
        id: this.selectedQuestionnaire.id,
        companyId: this.currentCompanyId,
      });

      if (response.data.error) {
        this.$store.dispatch('application/setErrorNotification', {
          message: response.data.error,
        });
      } else {
        this.refreshQuestionnaires();
        this.$store.dispatch('application/setSuccessNotification', {
          message: "You've successfully removed the questionnaire.",
        });
        this.removeQuestionnaireConfirmationDialogOpened = false;
        this.$forceUpdate();
      }
    },
    async getAllQuestionnaireTemplates() {
      const response = await API.questionnaireTemplate.getAllQuestionnaireTemplatesInfo();
      this.questionnaireTemplates = response.data;
    },
    async refreshQuestionnaires() {
      this.tableLoading = true;
      const response = await API.questionnaire.getQuestionnairesForYear({
        companyId: this.currentCompanyId,
        year: this.selectedYear,
        full: false,
      });
      if (response.data.error) {
        this.$store.dispatch('application/setErrorNotification', {
          message: response.data.error,
        });
        this.yearQuestionnaires = [];
      } else {
        this.yearQuestionnaires = response.data;
        if (this.refreshTries === 1) {
          await this.sleep(3000);
          this.refreshQuestionnaires();
          this.refreshTries = 0;
        } else if (
          this.yearQuestionnaires.some(d => this.isLoading(d)) &&
          this.refreshTries < 10
        ) {
          await this.sleep(this.refreshTries * 1000);
          this.refreshQuestionnaires();
          this.refreshTries = this.refreshTries + 1;
        } else {
          this.refreshTries = 1;
        }
      }
      this.$forceUpdate();
      this.tableLoading = false;
    },
    sleep(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },
    openAddOwnerDialog() {
      this.confirmNewConversationDialogOpened = false;
      this.comingFromAddOwner = false;
      this.selectedGroupId = get(
        this.currentCompany,
        'user.firm_group.id',
        null,
      );
      this.addOwnerDialogOpened = true;
    },
    async openConfirmNewConfirmationDialogFromAddOwner() {
      this.addOwnerDialogOpened = false;
      this.confirmNewConversationDialogOpened = true;
      this.comingFromAddOwner = true;
    },
    async addOwner(
      shouldCopyOverPreviousMessages,
      shouldTransferOverPreviousMessages,
    ) {
      try {
        this.addOwnerDialogOpened = false;
        this.comingFromAddOwner = false;
        this.confirmNewConversationDialogOpened = false;

        const response = await API.company.createNewOwner({
          email: this.newOwnerEmail,
          firstName: this.capitalizeWords(this.newOwnerFirstName),
          lastName: this.capitalizeWords(this.newOwnerLastName),
          phoneNumber: this.newOwnerPhoneNumber,
          companyId: this.currentCompanyId,
          groupId: this.selectedGroupId,
          shouldCopyOverPreviousMessages,
          shouldTransferOverPreviousMessages,
        });

        await this.getCurrentCompany();
        await this.$store.dispatch('companies/getCompanies');

        this.newOwnerIds = this.currentCompany.owner_ids_without_spouse;

        if (response.data.error || response.data.errors) {
          if (response.data.error.message === 'EMAIL_EXISTS') {
            this.addOwnerDialogOpened = true;
            this.$store.dispatch('application/setErrorNotification', {
              message: 'Email is already taken.',
            });
          } else if (response.data.error.message === 'INVALID_EMAIL') {
            this.addOwnerDialogOpened = true;
            this.$store.dispatch('application/setErrorNotification', {
              message: 'Email is invalid.',
            });
          } else {
            if (response.data.errors) {
              this.$store.dispatch('application/setErrorNotification', {
                message: response.data.errors,
              });
            } else {
              this.$store.dispatch('application/setErrorNotification', {
                message: response.data.error,
              });
            }
          }
        } else {
          await this.updateOwners(
            shouldCopyOverPreviousMessages,
            shouldTransferOverPreviousMessages,
          );
          await this.getAllClients();

          this.$store.dispatch('application/setSuccessNotification', {
            message: 'You have successfully added the new owner.',
          });
        }
      } catch (e) {
        if (
          e.response &&
          e.response.data &&
          e.response.data.includes('EMAIL_EXISTS')
        ) {
          this.addOwnerDialogOpened = true;
          this.$store.dispatch('application/setErrorNotification', {
            message: 'Email is already taken.',
          });
        } else {
          this.$store.dispatch('application/setErrorNotification', {
            message: 'Something went wrong.',
            errorToSend: e,
            userEmail: this.currentUser.email,
          });
        }

        console.log(e);
      } finally {
        this.tableLoading = false;
      }
    },
    async getAllReportTemplates() {
      const response = await API.report.getAllReportTemplates();
      this.reportTemplates = response.data;
    },

    getDocumentName(source) {
      return source.split('/')[source.split('/').length - 1];
    },
  },

  beforeDestroy() {
    document.title = this.previousTitle;
  },

  async created() {
    if (this.$route.query.companyId) {
      this.currentCompanyId = parseInt(this.$route.query.companyId);
    } else if (this.$route.query.company_id) {
      this.currentCompanyId = parseInt(this.$route.query.company_id);
    } else if (this.$route.params.hash) {
      const hash = this.$route.params.hash;
      const hashId = hash.substring(hash.lastIndexOf('-') + 1);
      const { data } = await API.company.getCompanyIdByHashId(hashId);

      this.currentCompanyId = data.id;
    } else {
      this.currentCompanyId = this.defaultCompany.id;
    }

    try {
      this.pageLoaded = false;
      await this.getCurrentCompany();

      this.previousTitle = document.title;
      document.title = this.currentCompany.name;

      await this.$store.dispatch('resetTiles', {
        hashId: this.currentCompany.hash_id,
        clientName: this.currentCompany.name
          .replaceAll(' ', '-')
          .replaceAll(',', '')
          .replaceAll('#', ''),
      });

      await this.refreshQuestionnaires();
      this.userContactTags = !isEmpty(this.currentCompany.user.contact_tags)
        ? this.currentCompany.user.contact_tags.map(c => c.name)
        : [];
      this.entityTags = !isEmpty(this.currentCompany.entity_tags)
        ? this.currentCompany.entity_tags.map(c => c.name)
        : [];
      this.getAllQuestionnaireTemplates();
      this.getAllFirmUsers();
      this.getAllEntityTypes();
      this.getAllClients();
      this.getAllQuestionnaireYears();
      this.getAllReportTemplates();
      this.newOwnerIds = this.currentCompany.owner_ids_without_spouse;

      try {
        const defaultYear = this.currentUser.overview_setting.default_year;
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();

        // If the current year is, for example, 2024 and a user's overview setting
        // has not been updated since 2022, we want to default to 'All'.
        if (typeof defaultYear === 'number' && defaultYear >= currentYear - 1) {
          this.selectedYear = defaultYear.toString();
        } else {
          this.selectedYear = 'All';
        }
      } catch (_) {
        this.selectedYear = 'All';
      }

      if (this.$route.query.questionnaireId) {
        const questionnaire = this.yearQuestionnaires.find(q => {
          return q.id === parseInt(this.$route.query.questionnaireId);
        });

        if (questionnaire) {
          this.openQuestionnaireDialog(questionnaire, this.$route.query.tab);
        }
      } else if (this.$route.query.prior_year_items_year) {
        this.priorYearItemsDialogOpened = true;
        this.rollOverYear = parseInt(this.$route.query.prior_year_items_year);
      } else if (this.$route.query.review_required) {
        this.reviewReturnDetailSyncDialogOpened = true;
      }
    } catch (e) {
      console.log(e);
    } finally {
      // this.$store.commit('application/END_LOADING');
      this.pageLoaded = true;
    }
  },

  watch: {
    companies: async function() {
      await this.getAllEntityTypes();
    },
    sendOpenItemsDialogOpened(val) {
      if (!val) {
        this.selectedOpenItemQuestionnaire = null;
      }
    },
    selectedYear: {
      async handler(year) {
        this.refreshQuestionnaires();
      },
      immediate: false,
    },
  },
};
</script>

<style lang="postcss">
.dashboard {
  &__label {
    color: #6f839c;
    font-size: 13px;
    font-weight: 100;
  }
  &__value {
    color: #0a1a25;
    font-size: 16px;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  &__button {
    color: rgba(0, 0, 0, 0.5);
    font-size: 14px;
    i {
      font-size: 16px;
    }
  }
}
</style>

<style lang="postcss" scoped>
.bordered {
  border: solid 2px #4f7eea;
  box-sizing: border-box;
}

.menu-icon {
  font-size: 15px;
}
.expansion-panel-content {
  border: 1px solid rgba(9, 30, 66, 0.06);
}
.bottom-bordered {
  border-bottom: 1px solid #bdbdbd;
}
.theme--light.v-expansion-panel .v-expansion-panel__container {
  border: solid 0.2px rgba(9, 30, 66, 0.06) !important;
}
.organized__document {
  cursor: pointer;
  padding-left: 15px;
  margin-bottom: 7px;
  padding-top: 2px;
  padding-bottom: 2px;
  padding-right: 15px;
  border: 1px solid rgba(9, 30, 66, 0.06);
}
.outlined {
  border: solid 0.2px rgba(9, 30, 66, 0.3) !important;
}
.active-type-select {
  max-width: 300px;
  margin: auto;
}
</style>
